import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactDOM from 'react-dom';
import type { FishIQRegion, FishIQSpecies, FishIQTab, LogEntry, UserProfile } from '../types';
import { useFishIQ } from '../hooks/useFishIQ';
import FishIQSpeciesCard, { FishIQSpeciesSummaryCard } from './FishIQSpeciesCard';
import FishIQDetailView from './FishIQDetailView';
import { fishIQGetSignalsNearMe } from '../services/fishIQClient';
import RoddoPageShell from './layout/RoddoPageShell';
import FishIQPilotCard from './FishIQPilotCard';
import { getRoddoUiTokens, resolveRoddoDarkMode } from '../constants/theme';
import { FISHIQ_SPECIES_SEED } from '../constants/fishIQSeed';
import {
  FISHIQ_REGIONAL_PROFILES,
  getFishIQRegionalProfilesForContext,
  normalizeFishIQSpeciesKey,
} from '../constants/fishIQRegionalProfiles';
import {
  buildFishIQLocationContext,
  getFishIQRankBucketSummary,
  rankFishIQSpeciesForLocation,
} from '../services/fishIQLocationRanker';
import { buildFishIQWaterDebug } from '../services/fishIQWaterContext';
import {
  consumePendingCatchFeedback,
  getSignalDelta,
  setSignalSnapshot,
  type PendingCatchFeedback,
} from '../utils/fishIQFeedback';
import { completeDailyTaskAction } from '../services/dailyRewards';
import { buildFishIQAreaRead } from '../lib/fishiq';
import { dedupeFishIQSpeciesForPresentation } from '../data/fishIQCanonicalSpecies';
import { trackRoddoEvent } from '../services/analytics';


const roddoTactilePolishStyles = `
  [data-roddo-tactile] button,
  [data-roddo-tactile] [role="button"],
  [data-roddo-tactile] a,
  [data-roddo-tactile] label[for],
  [data-roddo-tactile] select,
  [data-roddo-tactile] input,
  [data-roddo-tactile] textarea {
    -webkit-tap-highlight-color: transparent;
  }

  [data-roddo-tactile] button,
  [data-roddo-tactile] [role="button"],
  [data-roddo-tactile] a {
    transform: translateZ(0);
    transition:
      transform 180ms cubic-bezier(0.22, 1, 0.36, 1),
      filter 180ms cubic-bezier(0.22, 1, 0.36, 1),
      box-shadow 220ms cubic-bezier(0.22, 1, 0.36, 1),
      opacity 180ms cubic-bezier(0.22, 1, 0.36, 1),
      background 220ms cubic-bezier(0.22, 1, 0.36, 1),
      border-color 220ms cubic-bezier(0.22, 1, 0.36, 1);
    will-change: transform, filter;
  }

  [data-roddo-tactile] button:hover,
  [data-roddo-tactile] [role="button"]:hover,
  [data-roddo-tactile] a:hover {
    transform: translateY(-1px) translateZ(0);
    filter: brightness(1.035) saturate(1.035);
  }

  [data-roddo-tactile] button:active,
  [data-roddo-tactile] [role="button"]:active,
  [data-roddo-tactile] a:active {
    transform: translateY(1px) scale(0.97) translateZ(0);
    filter: brightness(0.985) saturate(0.98);
  }

  [data-roddo-tactile] button:focus-visible,
  [data-roddo-tactile] [role="button"]:focus-visible,
  [data-roddo-tactile] a:focus-visible,
  [data-roddo-tactile] input:focus-visible,
  [data-roddo-tactile] textarea:focus-visible,
  [data-roddo-tactile] select:focus-visible {
    outline: none;
    box-shadow:
      0 0 0 3px color-mix(in srgb, var(--roddo-accent-aqua-pop, #67e8f9) 26%, transparent),
      0 0 20px color-mix(in srgb, var(--roddo-accent-plum-pop, #c4b5fd) 18%, transparent);
  }


  /* Native select popup hardening.
     Chrome/Android can render <option> popups as bright white even while the
     select button is dark, which made the menu text look washed out. Keep the
     OS popup readable in both themes. */
  [data-roddo-tactile] select {
    color-scheme: light;
  }

  html.dark [data-roddo-tactile] select,
  .dark [data-roddo-tactile] select {
    color-scheme: dark;
  }

  [data-roddo-tactile] select option,
  [data-roddo-tactile] select optgroup {
    background: #fffaf2 !important;
    color: #241f1a !important;
    font-weight: 800;
  }

  [data-roddo-tactile] select option:checked {
    background: #dbeafe !important;
    color: #173f7a !important;
  }

  [data-roddo-tactile] select option:disabled {
    color: #7c6f64 !important;
  }

  html.dark [data-roddo-tactile] select option,
  html.dark [data-roddo-tactile] select optgroup,
  .dark [data-roddo-tactile] select option,
  .dark [data-roddo-tactile] select optgroup {
    background: #0C1423 !important;
    color: #F4F6FA !important;
  }

  html.dark [data-roddo-tactile] select option:checked,
  .dark [data-roddo-tactile] select option:checked {
    background: #8A70F2 !important;
    color: #ffffff !important;
  }

  html.dark [data-roddo-tactile] select option:disabled,
  .dark [data-roddo-tactile] select option:disabled {
    color: #7F8BA0 !important;
  }

  [data-roddo-tactile] .roddo-polish-card,
  [data-roddo-tactile] article {
    transition:
      transform 220ms cubic-bezier(0.22, 1, 0.36, 1),
      filter 220ms cubic-bezier(0.22, 1, 0.36, 1),
      box-shadow 260ms cubic-bezier(0.22, 1, 0.36, 1);
  }

  [data-roddo-tactile] .roddo-polish-card:hover,
  [data-roddo-tactile] article:hover {
    transform: translateY(-1px);
    filter: saturate(1.025) brightness(1.015);
  }

  [data-roddo-tactile] .roddo-polish-energy {
    position: relative;
    isolation: isolate;
    overflow: hidden;
  }

  [data-roddo-tactile] .roddo-polish-energy::before {
    content: '';
    position: absolute;
    inset: 0 0 auto 0;
    height: 3px;
    pointer-events: none;
    opacity: 0.72;
    background: linear-gradient(90deg,
      transparent,
      rgba(103,216,232,0.26),
      transparent
    );
    transform: translateX(-18%);
    transition: opacity 220ms ease, transform 420ms cubic-bezier(0.22, 1, 0.36, 1);
  }

  [data-roddo-tactile] .roddo-polish-energy:hover::before {
    opacity: 0.95;
    transform: translateX(0%);
  }

  [data-roddo-theme="light"] [data-roddo-tactile] .roddo-polish-energy::before,
  [data-roddo-tactile][data-roddo-theme="light"] .roddo-polish-energy::before {
    opacity: 0.50;
  }

  
.roddo-result-reveal {
  opacity: 0;
  animation: roddoFishiqResultReveal 480ms cubic-bezier(0.22, 1, 0.36, 1) forwards;
}
@keyframes roddoFishiqResultReveal {
  0% { opacity: 0; transform: translateY(10px) scale(0.992); filter: blur(1px); }
  100% { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); }
}

@media (prefers-reduced-motion: reduce) {
    [data-roddo-tactile] button,
    [data-roddo-tactile] [role="button"],
    [data-roddo-tactile] a,
    [data-roddo-tactile] .roddo-polish-card,
    [data-roddo-tactile] article {
      transition: none !important;
      transform: none !important;
      filter: none !important;
    }
  }
`;

interface FishIQViewProps {
  userProfile: UserProfile | null;
  catchLog: LogEntry[];
  onBack: () => void;
  onSelectSpecies?: (speciesName: string, payload?: any) => void;
  initialQuery?: string;
  mapContext?: {
  source?: string | null;
  coords?: { lat: number; lon: number } | null;
  waterType?: string | null;
  waterKind?: string | null;
  waterName?: string | null;
  regionHint?: string | null;
} | null;
  communityPattern?: {
    sourcePostId?: string | null;
    species?: string | null;
    waterName?: string | null;
    waterType?: string | null;
    technique?: string | null;
    lureOrBait?: string | null;
    timeBucket?: string | null;
    conditions?: string[];
    patternKey?: string | null;
    regionKey?: string | null;
    signalScore?: number | null;
  } | null;
  coords?: { lat: number; lon: number } | null;
  addToast?: (message: string, type?: any) => void;
  isDarkMode?: boolean;
}


type AquaticFilter = 'all' | 'fish' | 'shellfish';
type WaterTypeFilter = 'all' | 'freshwater' | 'saltwater' | 'brackish';
type FishIQStudyRegionId = 'around' | 'all' | string;

const FISHIQ_STUDY_REGION_STORAGE_KEY = 'roddo_fishiq_study_region_v1';

type FishIQSignal = {
  id: string;
  geo5: string;
  timeBucket: string;
  speciesKey: string;
  waterKey: string;
  waterTypeKey?: string;
  count24h: number;
  uniqueUsers24h: number;
  lastSeenAt: number | null;
  topTechniques: string[];
  topLures: string[];
  topWaterTypes: string[];
  activityScore?: number;
  activityRank?: number;
  activityBand?: string;
  regulatoryFlags?: string[];
};

const prettyKey = (s: any) => {
  const x = String(s ?? '').trim();
  if (!x || x === 'unknown') return '—';
  return x
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (m) => m.toUpperCase());
};


const getSignalActivityLabel = (index: number, signal?: Partial<FishIQSignal> | null) => {
  const band = String(signal?.activityBand || '').toLowerCase();
  if (band === 'most_active') return 'Most active';
  if (band === 'active') return 'Active';
  if (band === 'possible') return 'Habitat fit';
  if (index <= 0) return 'Most active';
  if (index === 1) return 'Active';
  if (index === 2) return 'Range';
  return 'Species guide';
};

const getSignalRuleLabel = (signal?: Partial<FishIQSignal> | null) => {
  const flags = Array.isArray(signal?.regulatoryFlags) ? signal.regulatoryFlags.map((x) => String(x).toLowerCase()) : [];
  const species = String(signal?.speciesKey || '').toLowerCase();
  const kind = String((signal as any)?.aquaticKind || '').toLowerCase();
  const shellfish =
    flags.includes('shellfish') ||
    ['crustacean', 'mollusk', 'cephalopod', 'echinoderm'].includes(kind) ||
    /(crab|shrimp|prawn|lobster|crawfish|crayfish|clam|oyster|mussel|scallop|squid|octopus|abalone|urchin)/i.test(species);
  if (flags.includes('closures')) return 'Check closures';
  if (shellfish || flags.includes('check_rules') || flags.includes('regulated')) return 'Check rules';
  return null;
};

const normalizeList = (input: any): string[] => {
  if (!Array.isArray(input)) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const item of input) {
    const value = String(item ?? '').trim();
    if (!value) continue;
    const key = value.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(value);
  }
  return out;
};

const inferWaterTypes = (raw: any, fallbackWaterKey?: string, fallbackWaterTypeKey?: string): string[] => {
  const candidates = normalizeList(
    raw?.topWaterTypes ?? raw?.waterTypes ?? raw?.topWaterTypeKeys ?? raw?.waterTypeKeys ?? [],
  );
  if (candidates.length) return candidates;
  const fallback = normalizeList([fallbackWaterTypeKey, fallbackWaterKey]);
  return fallback.filter((x) => x && x !== 'unknown');
};


const collectSpeciesWaterText = (item: any): string => {
  const fishiq = item?.__fishiq || {};
  const rank = fishiq?.locationRank || {};
  const signalMeta = fishiq?.signalMeta || {};
  const values = [
    item?.waterTypeKey,
    item?.waterName,
    item?.waterType,
    item?.waterKind,
    item?.habitat,
    item?.habitats,
    item?.waterTypes,
    item?.topWaterTypes,
    item?.range,
    item?.regions,
    item?.environment,
    item?.environments,
    item?.salinity,
    item?.preferredWater,
    item?.preferredWaters,
    signalMeta?.waterKey,
    signalMeta?.waterTypeKey,
    rank?.bucket,
    rank?.reasons,
  ];

  return values
    .flatMap((value) => (Array.isArray(value) ? value : [value]))
    .filter((value) => value !== null && value !== undefined)
    .map((value) => String(value).toLowerCase())
    .join(' ');
};

const getSpeciesWaterAffinity = (item: any) => {
  const text = collectSpeciesWaterText(item);
  const hasBrackish = /\b(brackish|estuary|estuarine|mangrove|marsh|lagoon|delta|tidal|backwater|river mouth|rivermouth|bay|harbor|harbour)\b/i.test(text);
  const hasSalt = /\b(saltwater|salt water|marine|ocean|sea|coast|coastal|inshore|offshore|nearshore|surf|beach|reef|kelp|pier|jetty|bay|harbor|harbour|estuary|estuarine|mangrove|lagoon|tidal)\b/i.test(text);
  const hasFresh = /\b(freshwater|fresh water|lake|reservoir|pond|river|stream|creek|brook|canal|inland|headwater|tailwater|tributary|spring)\b/i.test(text);

  return {
    freshwater: hasFresh,
    saltwater: hasSalt,
    brackish: hasBrackish || (hasFresh && hasSalt),
  };
};

const speciesMatchesWaterTypeFilter = (item: any, filter: WaterTypeFilter): boolean => {
  if (filter === 'all') return true;
  const affinity = getSpeciesWaterAffinity(item);
  return affinity[filter] === true;
};

const fishIQSpeciesRegionalKeys = (item: any): string[] => {
  const values = [
    item?.canonicalSpecies?.canonicalId,
    item?.canonicalSpeciesId,
    item?.speciesId,
    item?.id,
    item?.slug,
    item?.commonName,
    item?.name,
    item?.scientificName,
    item?.family,
    ...(Array.isArray(item?.aliases) ? item.aliases : []),
    ...(Array.isArray(item?.canonicalSpecies?.legacyIds) ? item.canonicalSpecies.legacyIds : []),
    ...(Array.isArray(item?.legacyIds) ? item.legacyIds : []),
  ];

  return Array.from(new Set(values.map(normalizeFishIQSpeciesKey).filter(Boolean)));
};

const fishIQProfileCandidateKeys = (profile: any): Set<string> => {
  const keys = (Array.isArray(profile?.rules) ? profile.rules : [])
    .flatMap((rule: any) => Array.isArray(rule?.speciesKeys) ? rule.speciesKeys : [])
    .map(normalizeFishIQSpeciesKey)
    .filter(Boolean);
  return new Set(keys);
};

const filterFishIQSpeciesToProfile = (items: any[], profile: any | null): any[] => {
  if (!profile) return items;
  const candidateKeys = fishIQProfileCandidateKeys(profile);
  if (!candidateKeys.size) return items;
  return items.filter((item: any) => fishIQSpeciesRegionalKeys(item).some((key) => candidateKeys.has(key)));
};

const getFishIQStudyRuleForSpecies = (item: any, profile: any | null): any | null => {
  if (!profile || !Array.isArray(profile?.rules)) return null;
  const speciesKeys = new Set(fishIQSpeciesRegionalKeys(item));
  if (!speciesKeys.size) return null;

  const matches = profile.rules.filter((rule: any) =>
    (Array.isArray(rule?.speciesKeys) ? rule.speciesKeys : [])
      .map(normalizeFishIQSpeciesKey)
      .some((key: string) => speciesKeys.has(key)),
  );

  if (!matches.length) return null;
  return [...matches].sort((a: any, b: any) => Number(b?.boost || 0) - Number(a?.boost || 0))[0] || null;
};

const isFishIQFeaturedStudyRule = (rule: any) => {
  const label = String(rule?.label || '').toLowerCase();
  return label.includes('strong regional') || label.includes('common regional');
};


const normalizeKey = (value: any) => String(value ?? '').trim().toLowerCase();

const getFishIQSavedKeys = (item: any): string[] => {
  const rawKeys = [
    item?.id,
    item?.slug,
    item?.key,
    item?.name,
    item?.label,
    item?.commonName,
  ];

  return Array.from(
    new Set(
      rawKeys
        .map(normalizeKey)
        .filter(Boolean),
    ),
  );
};

const isFishIQItemSaved = (item: any, savedIds?: string[]) => {
  const savedKeySet = new Set((savedIds || []).map(normalizeKey).filter(Boolean));
  if (!savedKeySet.size) return false;

  return getFishIQSavedKeys(item).some((key) => savedKeySet.has(key));
};

const getFishIQSavedToggleKey = (item: any, savedIds?: string[]) => {
  const savedKeySet = new Set((savedIds || []).map(normalizeKey).filter(Boolean));
  const existingKey = getFishIQSavedKeys(item).find((key) => savedKeySet.has(key));

  if (existingKey) {
    return (savedIds || []).find((value) => normalizeKey(value) === existingKey) || existingKey;
  }

  return String(item?.id || item?.slug || item?.commonName || item?.name || '');
};

type FishIQLaunchRegionFilter = {
  key: string;
  label: string;
  shortLabel?: string;
  group: 'Pilot' | 'Catalog' | 'Growth';
  emoji: string;
  aliases: string[];
  summary: string;
  topSpecies: string[];
  waterways: string[];
  pattern: string;
  starterMoves: string[];
  note: string;
};

const FISHIQ_LAUNCH_REGION_FILTERS: FishIQLaunchRegionFilter[] = [
  {
    key: 'socal',
    label: 'Southern California',
    shortLabel: 'SoCal',
    group: 'Pilot',
    emoji: '🌴',
    aliases: ['socal', 'southern california', 'southern_california', 'los angeles', 'orange county', 'san diego'],
    summary: 'Roddo’s home pilot area: surf, harbor, bay, kelp, and nearshore reads around Southern California waters.',
    topSpecies: ['California Halibut', 'Surfperch', 'Spotted Bay Bass', 'Kelp Bass'],
    waterways: ['Surf troughs', 'Harbor channels', 'Kelp / rocky edges'],
    pattern: 'Start by matching the water type: sandy surf and harbor mouths lean halibut/surfperch, while docks, eelgrass, rocks, and kelp edges lean bass, sheephead, white seabass, and nearshore structure fish.',
    starterMoves: ['Fish moving tide around harbor mouths and channel edges.', 'Use surf structure first: troughs, cuts, holes, and sand crab zones.', 'Around kelp or rock, slow down and work edges instead of open water.'],
    note: 'Roddo home pilot area. Use the water read for planning, then check current CDFW/local rules before harvest.',
  },
  {
    key: 'united_states',
    label: 'United States catalog',
    shortLabel: 'United States',
    group: 'Catalog',
    emoji: '🇺🇸',
    aliases: ['united states', 'usa', 'u.s.', 'us catalog', 'america', 'north america'],
    summary: 'Broad U.S. freshwater, inshore, surf, reef, and offshore species coverage for search and education.',
    topSpecies: ['Largemouth Bass', 'Rainbow Trout', 'Redfish', 'Striped Bass'],
    waterways: ['Lakes / reservoirs', 'Rivers / streams', 'Inshore / offshore'],
    pattern: 'This is a broad U.S. library, not one local bite call. Use it when you want to search species, compare habitats, or let Big Blue/map context narrow the list by water type.',
    starterMoves: ['Freshwater: split warm-water bass/panfish/catfish from cold-water trout/salmonids.', 'Coasts: separate inshore bays and surf from reef, wreck, and offshore pelagic patterns.', 'Use the species card details for bait, timing, and habitat before planning a trip.'],
    note: 'Broad education/search catalog only. State rules, seasons, closures, and limits must come from official local agencies.',
  },
  {
    key: 'us_gulf_coast',
    label: 'U.S. Gulf Coast',
    shortLabel: 'Gulf Coast',
    group: 'Catalog',
    emoji: '🌊',
    aliases: [
      'gulf',
      'gulf coast',
      'u.s. gulf',
      'us gulf',
      'gulf of mexico',
      'texas coast',
      'louisiana coast',
      'mississippi coast',
      'alabama coast',
      'florida gulf',
    ],
    summary: 'Warm Gulf of Mexico inshore, bay, marsh, reef, and nearshore patterns for broad education and trip planning.',
    topSpecies: ['Redfish', 'Speckled Trout', 'Flounder', 'Red Snapper'],
    waterways: ['Marsh drains', 'Grass / shell flats', 'Nearshore reefs'],
    pattern: 'Think tide, bait, and bottom type. Marsh drains and grass/shell edges lean redfish, trout, black drum, and flounder; reef and wreck structure shifts the read toward snapper, grouper, cobia, mackerel, and amberjack.',
    starterMoves: ['On moving tide, check drains, points, oyster/shell edges, and bait lanes.', 'For flounder, slow-roll bottom near passes, cuts, and sandy drop-offs.', 'For reef fish, structure and legal depth/season matter more than a generic region label.'],
    note: 'Broad Gulf of Mexico guide only. Always verify current Texas/Louisiana/Mississippi/Alabama/Florida rules before harvest.',
  },
  {
    key: 'thailand',
    label: 'Thailand',
    group: 'Growth',
    emoji: '🇹🇭',
    aliases: ['thailand', 'thai', 'bangkok', 'phuket', 'andaman sea', 'pla chon', 'pla kapong'],
    summary: 'Freshwater snakehead, barramundi, carp/catfish giants, coastal jacks, mackerel, snapper, grouper, and sailfish.',
    topSpecies: ['Giant Snakehead', 'Barramundi', 'Queenfish', 'Narrow-barred Spanish Mackerel'],
    waterways: ['Canals / reservoirs', 'Estuaries / mangroves', 'Andaman / Gulf coasts'],
    pattern: 'Freshwater reads lean snakehead, walking catfish, tilapia, carp, and stocked/private-lake giants. Brackish structure can bring barramundi/mangrove jack patterns. Coastal water shifts toward queenfish, GT, mackerel, snapper, grouper, and sailfish.',
    starterMoves: ['For snakehead, look for vegetation, backwaters, surface activity, and low-light ambush windows.', 'For barramundi, start around current, mangrove edges, bridges, river mouths, and bait.', 'Treat Mekong giants and freshwater stingray-style fish as education/sensitive context unless the venue is clearly legal and appropriate.'],
    note: 'Growth-region guide, not NOAA pilot data. Local access, protected species, and venue rules matter heavily.',
  },
  {
    key: 'philippines',
    label: 'Philippines',
    group: 'Growth',
    emoji: '🇵🇭',
    aliases: ['philippines', 'philippine', 'bangus', 'lapu-lapu', 'maya-maya', 'tanigue', 'dalag', 'hito', 'apahap'],
    summary: 'Island fisheries coverage for offshore tuna/billfish, reef snapper/grouper, bangus, rabbitfish, barramundi, and local food fish.',
    topSpecies: ['Giant Trevally', 'Yellowfin Tuna', 'Bangus', 'Lapu-lapu'],
    waterways: ['Reefs / drop-offs', 'Offshore islands', 'Brackish ponds / rivers'],
    pattern: 'Island and reef context matters. Offshore/current edges lean tuna, GT, mahi, billfish, and wahoo; reefs lean snapper/grouper/rabbitfish; brackish and inland waters can surface bangus, tilapia, hito, dalag, and apahap-style reads.',
    starterMoves: ['For offshore pelagics, look for current, birds, bait, FAD/structure context, and cleaner water edges.', 'For reef species, match depth, reef edge, rubble/sand transitions, and local bait.', 'Use aliases when searching: bangus, lapu-lapu, maya-maya, tanigue, dalag, hito, and apahap.'],
    note: 'Philippines pack is a growth/education layer. Local names vary by island and market usage, so Roddo should stay humble.',
  },
  {
    key: 'india',
    label: 'India',
    group: 'Growth',
    emoji: '🇮🇳',
    aliases: ['india', 'indian', 'rohu', 'catla', 'mrigal', 'mahseer', 'murrel', 'seer fish', 'surmai', 'vanjaram', 'bhetki', 'karimeen'],
    summary: 'Water-first India coverage for major-carp reservoirs, river mahseer, murrel/canal warmwater, backwaters and estuaries, west/east coasts, reefs, and offshore reads.',
    topSpecies: ['Rohu', 'Mahseer', 'Murrel', 'Seer Fish'],
    waterways: ['Rivers / reservoirs', 'Ponds / canals', 'Backwaters / coasts'],
    pattern: 'Start with the water. Reservoirs and ponds lean major carps and warmwater fish; rivers can shift toward mahseer and large catfish; canals and flooded margins favor murrel/snakehead profiles. Backwaters and estuaries bring karimeen, bhetki, threadfin, and mangrove species into play, while Arabian Sea/Bay of Bengal and reef/offshore context shifts toward mackerel, seer fish, queenfish, GT, and other marine targets.',
    starterMoves: ['Inland: separate stillwater carp water from flowing mahseer/current habitat and shallow murrel/canal cover.', 'Coastal: treat Kerala-style backwaters and estuaries differently from surf, reef, and offshore water.', 'Use local aliases when searching: rohu, catla, mrigal, mahseer, murrel, surmai, vanjaram, anjal, bhetki, rawas, and karimeen.'],
    note: 'India pack is a growth/education layer. Monsoon, river discharge, salinity, coast, and local names vary heavily, so Roddo keeps confidence tied to the actual water context.',
  },
  {
    key: 'indonesia',
    label: 'Indonesia',
    group: 'Growth',
    emoji: '🇮🇩',
    aliases: ['indonesia', 'bali', 'java', 'sumatra', 'papua', 'bandeng', 'baronang', 'patin'],
    summary: 'Archipelago coverage for GT, tuna, reef fish, barramundi, mangrove jack, snakehead, mahseer, hampala, baung, milkfish, and tilapia, with island/drainage context kept conservative.',
    topSpecies: ['Giant Trevally', 'Dogtooth Tuna', 'Toman / Giant Snakehead', 'Bandeng'],
    waterways: ['Reef drop-offs', 'Mangrove estuaries', 'Rivers / jungle lakes'],
    pattern: 'Indonesia needs island-level context. Reef and bluewater edges can surface GT, dogtooth/yellowfin tuna, wahoo, snapper, grouper, and coral trout; estuaries lean barramundi/mangrove jack; freshwater reads shift by island and drainage, with gabus widespread and toman confidence strongest in Sumatra/Borneo while hampala, mahseer, baung, tilapia, and other targets remain water-specific.',
    starterMoves: ['For GT/dogtooth, prioritize current, reef points, drop-offs, bait, and tide movement.', 'For barramundi and mangrove jack, start around mangroves, river mouths, snags, and dirty/clean water edges.', 'For freshwater, trust island/drainage + water type before a country-wide match; toman should be strongest around Sumatra/Borneo rather than every Indonesian water.'],
    note: 'Indonesia varies heavily by island, coast, river, and access. Use Big Blue/location context before trusting any broad read.',
  },
] as const;

const buildFishIQLaunchRegionCard = (filter: FishIQLaunchRegionFilter): FishIQRegion => ({
  id: `fishiq_region_${filter.key}`,
  name: filter.label,
  title: `${filter.emoji} ${filter.label}`,
  scope: 'checklist',
  summary: filter.summary,
  topSpecies: filter.topSpecies,
  waterways: filter.waterways,
  pattern: filter.pattern,
  starterMoves: filter.starterMoves,
  note: filter.note,
  seoSourceKey: `fishiq-region-${filter.key}`,
  ...(filter.group === 'Growth' ? { country: filter.label } : {}),
  ...(filter.key === 'socal' ? { country: 'United States', stateOrProvince: 'California' } : {}),
  aliases: [filter.key, filter.label, filter.shortLabel, filter.group, ...filter.aliases].filter(Boolean),
} as FishIQRegion & { aliases: string[]; note: string });

const FISHIQ_NOISY_REGION_RE =
  /\b(introduced|worldwide|stocked worldwide|stocked-worldwide|global|cosmopolitan|native range|non-native|invasive|temperate|subtropical|tropical|aquaculture|farm|farm-raised)\b/i;

const getFishIQRegionText = (region: any) =>
  [
    region?.id,
    region?.slug,
    region?.key,
    region?.name,
    region?.label,
    region?.title,
    ...(Array.isArray(region?.aliases) ? region.aliases : []),
    ...(Array.isArray(region?.regionHints) ? region.regionHints : []),
    ...(Array.isArray(region?.tags) ? region.tags : []),
  ]
    .map((value) => String(value ?? '').trim())
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

const normalizeFishIQRegionText = (value: any) =>
  String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/[()]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const regionTextHasAlias = (rawText: string, rawAlias: any) => {
  const text = normalizeFishIQRegionText(rawText);
  const alias = normalizeFishIQRegionText(rawAlias);
  if (!text || !alias) return false;

  // Short/common tokens like "gulf", "usa", "us", and "thai" need whole-word matching.
  // This prevents broad searches like "gulf" from leaking into longer unrelated phrases.
  if (!alias.includes(' ') || alias.length <= 4) {
    return new RegExp(`(^|\\s)${escapeRegExp(alias)}(\\s|$)`, 'i').test(text);
  }

  return text.includes(alias);
};

const getFishIQLaunchRegion = (region: any) => {
  const idText = normalizeFishIQRegionText(region?.id || region?.key || region?.slug || '');
  const directMatch = FISHIQ_LAUNCH_REGION_FILTERS.find(
    (filter) => idText === filter.key || idText === `fishiq region ${filter.key}` || idText === `fishiq_region_${filter.key}`,
  );
  if (directMatch) return directMatch;

  const text = getFishIQRegionText(region);
  if (!text || FISHIQ_NOISY_REGION_RE.test(text)) return null;

  return (
    FISHIQ_LAUNCH_REGION_FILTERS.find((filter) => {
      const aliases = [filter.key, filter.label, filter.shortLabel, ...filter.aliases].filter(Boolean);
      return aliases.some((alias) => regionTextHasAlias(text, alias));
    }) || null
  );
};

const getFishIQRegionDisplayName = (region: any) =>
  getFishIQLaunchRegion(region)?.label || region?.name || region?.label || region?.title || 'Region';


const speciesMatchesLocalQuery = (item: any, rawQuery: string): boolean => {
  const q = normalizeKey(rawQuery);
  if (!q) return true;

  // Species search is intentionally identity-only.
  // Do not search bait, prey, habitat, summaries, regions, or tactics here:
  // typing "shrimp" should find the Shrimp card, not every fish that eats shrimp.
  const identityFields = [
    item?.id,
    item?.slug,
    item?.commonName,
    item?.name,
    item?.scientificName,
    item?.taxonomy?.scientificName,
    item?.family,
    item?.canonicalSpecies?.canonicalId,
    item?.canonicalSpecies?.normalizedScientificName,
    item?.canonicalSpecies?.normalizedCommonName,
    ...(Array.isArray(item?.aliases) ? item.aliases : []),
    ...(Array.isArray(item?.canonicalSpecies?.aliases) ? item.canonicalSpecies.aliases : []),
    ...(Array.isArray(item?.canonicalSpecies?.legacyIds) ? item.canonicalSpecies.legacyIds : []),
  ];

  return identityFields.some((value) => normalizeKey(value).includes(q));
};

const mergeUniqueText = (...groups: any[]): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const group of groups) {
    const values = Array.isArray(group)
      ? group
      : typeof group === 'string'
        ? group.split(/\n|,|•|·/g)
        : [];
    for (const item of values) {
      const value = String(item ?? '').trim();
      if (!value) continue;
      const key = value.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(value);
    }
  }
  return out;
};

const matchSpeciesName = (value: any) =>
  String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');

const buildSpeciesMatchKeys = (item: any): string[] =>
  mergeUniqueText(
    [item?.id, item?.slug, item?.commonName, item?.name],
    Array.isArray(item?.aliases) ? item.aliases : [],
  ).map(matchSpeciesName);

const buildPrimarySignalRead = (signal: FishIQSignal | null, matchCount: number) => {
  if (!signal) return null;
  const water = prettyKey(signal.topWaterTypes?.[0] || signal.waterTypeKey || signal.waterKey);
  const technique = prettyKey(signal.topTechniques?.[0] || '');
  const lure = prettyKey(signal.topLures?.[0] || '');
  const lastSeen = timeAgoShort(signal.lastSeenAt);
  const countLabel = signal.count24h === 1 ? 'recent catch' : 'recent catches';

  if (water && technique && water !== '—') {
    return `Local signal is leaning toward ${water.toLowerCase()} water, with ${technique.toLowerCase()} reading like the cleaner first move.`;
  }
  if (water && lure && water !== '—') {
    return `Nearby activity often looks tied to ${water.toLowerCase()} water, with ${lure.toLowerCase()} getting the early nod.`;
  }
  if (matchCount > 0) {
    return `Recent local activity suggests this species is still in the mix here, with ${signal.count24h} ${countLabel} showing in the last ${lastSeen}.`;
  }
  return `Nearby activity is shown as field context layered over the documented species profile.`;
};

const buildSupportingSignalReads = (signal: FishIQSignal | null): string[] => {
  if (!signal) return [];
  const rows: string[] = [];
  if (signal.topWaterTypes?.length) rows.push(`Nearby water read leans ${signal.topWaterTypes.slice(0, 2).map(prettyKey).join(' · ')}.`);
  if (signal.topTechniques?.length) rows.push(`${prettyKey(signal.topTechniques[0])} is showing as the cleaner local presentation.`);
  if (signal.topLures?.length) rows.push(`${prettyKey(signal.topLures[0])} is surfacing as a sensible first pull.`);
  if (signal.waterKey && signal.waterKey !== 'unknown') rows.push(`Recent activity is clustering around ${prettyKey(signal.waterKey)}.`);
  if (signal.count24h > 0) rows.push(`${signal.count24h} nearby catches from ${signal.uniqueUsers24h} ${signal.uniqueUsers24h === 1 ? 'angler' : 'anglers'} in this bucket.`);
  return rows.slice(0, 4);
};

const getSpeciesSignal = (item: any, signals: FishIQSignal[]): FishIQSignal | null => {
  const matchKeys = new Set(buildSpeciesMatchKeys(item));
  if (!matchKeys.size) return null;
  return (
    signals.find((signal) => matchKeys.has(matchSpeciesName(signal.speciesKey))) ??
    null
  );
};


type FishIQAreaReadGroup = {
  key: string;
  title: string;
  emoji: string;
  tone: 'likely' | 'possible' | 'seasonal' | 'stocked' | 'quiet';
  species: string[];
};

const getAreaSpeciesName = (item: any) =>
  String(item?.commonName || item?.name || item?.id || '').trim();

const getAreaRankLabel = (item: any) =>
  String(item?.__fishiq?.locationRank?.shortLabel || item?.__fishiq?.locationRank?.label || '').trim();

const getAreaRankBucket = (item: any) =>
  String(item?.__fishiq?.locationRank?.bucket || '').trim();

const getAreaRankReasons = (item: any): string[] =>
  Array.isArray(item?.__fishiq?.locationRank?.reasons) ? item.__fishiq.locationRank.reasons : [];


const cleanFishIQAreaWaterLabel = (value: any) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  return raw
    .replace(/\b(likely|possible|species profile|range fit)\b/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
};

const classifyAreaReadGroup = (item: any): FishIQAreaReadGroup['key'] => {
  const label = getAreaRankLabel(item).toLowerCase();
  const bucket = getAreaRankBucket(item);
  const reasons = getAreaRankReasons(item).join(' ').toLowerCase();

  if (label.includes('stocked') || reasons.includes('stocked') || reasons.includes('agency')) return 'stocked';
  if (label.includes('seasonal') || bucket === 'seasonal') return 'seasonal';
  if (label.includes('strong regional') || label.includes('common regional') || label.includes('regional') || label.includes('habitat') || bucket === 'likely') return 'likely';
  if (label.includes('habitat') || label.includes('guide') || label.includes('check') || bucket === 'good_target' || bucket === 'possible') return 'possible';
  return 'quiet';
};

const buildFishIQAreaReadGroups = (items: any[]): FishIQAreaReadGroup[] => {
  const groups: FishIQAreaReadGroup[] = [
    { key: 'likely', title: 'Strong regional matches', emoji: '📚', tone: 'likely', species: [] },
    { key: 'stocked', title: 'Managed / stocked clue', emoji: '📍', tone: 'stocked', species: [] },
    { key: 'seasonal', title: 'Seasonal pattern', emoji: '🗓️', tone: 'seasonal', species: [] },
    { key: 'possible', title: 'Habitat matches', emoji: '🧭', tone: 'possible', species: [] },
    { key: 'quiet', title: 'Species guide', emoji: '📘', tone: 'quiet', species: [] },
  ];

  const byKey = new Map(groups.map((group) => [group.key, group]));
  const seen = new Set<string>();

  for (const item of items || []) {
    const name = getAreaSpeciesName(item);
    if (!name) continue;
    const normalizedName = normalizeKey(name);
    if (!normalizedName || seen.has(normalizedName)) continue;
    seen.add(normalizedName);

    const group = byKey.get(classifyAreaReadGroup(item)) || byKey.get('quiet');
    if (!group || group.species.length >= 5) continue;
    group.species.push(name);
  }

  return groups.filter((group) => group.species.length > 0);
};

const buildFishIQAreaReasonChips = (items: any[]): string[] => {
  const preferredOrder = [
    'lake/reservoir match',
    'river/current match',
    'bay/estuary match',
    'surf/coast match',
    'Southern California regional match',
    'spring timing fit',
    'summer timing fit',
    'fall timing fit',
    'winter timing fit',
    'CDFW/agency stocked-water signal',
    'Roddo catches nearby',
    'curated regional match',
  ];

  const found = new Set<string>();
  for (const item of items || []) {
    for (const reason of getAreaRankReasons(item)) {
      const value = String(reason || '').trim();
      if (value) found.add(value);
    }
  }

  return preferredOrder.filter((reason) => found.has(reason)).concat(
    Array.from(found).filter((reason) => !preferredOrder.includes(reason)),
  ).slice(0, 6);
};

const countViewerSpeciesCatches = (item: any, entries: any[]): number => {
  const matchKeys = new Set(buildSpeciesMatchKeys(item));
  if (!matchKeys.size || !Array.isArray(entries)) return 0;
  return entries.reduce((sum, entry) => {
    const speciesName = matchSpeciesName(entry?.species ?? entry?.speciesName ?? entry?.fishType ?? '');
    return sum + (speciesName && matchKeys.has(speciesName) ? 1 : 0);
  }, 0);
};


const uniqueText = (input: any, limit = 12): string[] => {
  const src = Array.isArray(input)
    ? input
    : typeof input === 'string'
      ? input.split(/\n|,|•|·/g)
      : [];

  const seen = new Set<string>();
  const out: string[] = [];
  for (const item of src) {
    const value = String(item ?? '').trim();
    if (!value) continue;
    const key = value.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(value);
    if (out.length >= limit) break;
  }
  return out;
};

const canonicalizeSpecies = (input: any) => {
  if (!input) return null;

  const commonName = String(input?.commonName ?? input?.name ?? '').trim();
  const scientificName = String(input?.scientificName ?? '').trim();
  const summary = String(input?.summary ?? input?.description ?? input?.notes ?? '').trim();
  const aliases = uniqueText(input?.aliases ?? []);
  const habitats = uniqueText(input?.habitats ?? input?.waterTypes ?? input?.topWaterTypes ?? []);
  const bestBaits = uniqueText(input?.tactics?.bestBaits ?? input?.bestBaits ?? input?.baits ?? []);
  const bestTechniques = uniqueText(input?.tactics?.bestTechniques ?? input?.bestTechniques ?? input?.techniques ?? []);
  const keyTraits = uniqueText(input?.keyTraits ?? []);

  return {
    ...input,
    id: String(input?.id ?? input?.slug ?? commonName).trim(),
    slug: String(input?.slug ?? input?.id ?? commonName).trim(),
    commonName: commonName || String(input?.title ?? 'Unknown species').trim(),
    name: commonName || String(input?.title ?? 'Unknown species').trim(),
    scientificName: scientificName || undefined,
    summary,
    description: summary || String(input?.description ?? '').trim(),
    notes: String(input?.notes ?? summary).trim(),
    aliases,
    habitats,
    waterTypes: habitats,
    bestBaits,
    bestTechniques,
    keyTraits,
    tactics: {
      ...(input?.tactics || {}),
      bestBaits,
      bestTechniques,
      bestSeasons: uniqueText(input?.tactics?.bestSeasons ?? input?.bestSeasons ?? []),
      bestTimesOfDay: uniqueText(input?.tactics?.bestTimesOfDay ?? input?.bestTimesOfDay ?? []),
    },
  };
};

const mergeSpeciesData = (live: any, canonical: any) => {
  const base = canonicalizeSpecies(canonical) || {};
  const next = canonicalizeSpecies(live) || {};
  if (!base && !next) return null;

  const commonName = String(next?.commonName ?? base?.commonName ?? next?.name ?? base?.name ?? '').trim();
  const scientificName = String(next?.scientificName ?? base?.scientificName ?? '').trim();
  const summary = String(next?.summary ?? base?.summary ?? next?.description ?? base?.description ?? '').trim();

  return {
    ...base,
    ...next,
    id: String(next?.id ?? base?.id ?? next?.slug ?? base?.slug ?? commonName).trim(),
    slug: String(next?.slug ?? base?.slug ?? next?.id ?? base?.id ?? commonName).trim(),
    commonName: commonName || 'Unknown species',
    name: commonName || 'Unknown species',
    scientificName: scientificName || undefined,
    summary,
    description: summary || String(next?.description ?? base?.description ?? '').trim(),
    notes: String(next?.notes ?? base?.notes ?? summary).trim(),
    aliases: uniqueText([...(base?.aliases || []), ...(next?.aliases || [])]),
    habitats: uniqueText([...(base?.habitats || []), ...(next?.habitats || []), ...(next?.waterTypes || []), ...(base?.waterTypes || [])]),
    waterTypes: uniqueText([...(next?.waterTypes || []), ...(base?.waterTypes || []), ...(next?.habitats || []), ...(base?.habitats || [])]),
    bestBaits: uniqueText([...(base?.bestBaits || []), ...(next?.bestBaits || []), ...(base?.tactics?.bestBaits || []), ...(next?.tactics?.bestBaits || [])]),
    bestTechniques: uniqueText([...(base?.bestTechniques || []), ...(next?.bestTechniques || []), ...(base?.tactics?.bestTechniques || []), ...(next?.tactics?.bestTechniques || [])]),
    keyTraits: uniqueText([...(base?.keyTraits || []), ...(next?.keyTraits || [])]),
    tactics: {
      ...(base?.tactics || {}),
      ...(next?.tactics || {}),
      bestBaits: uniqueText([...(base?.bestBaits || []), ...(next?.bestBaits || []), ...(base?.tactics?.bestBaits || []), ...(next?.tactics?.bestBaits || [])]),
      bestTechniques: uniqueText([...(base?.bestTechniques || []), ...(next?.bestTechniques || []), ...(base?.tactics?.bestTechniques || []), ...(next?.tactics?.bestTechniques || [])]),
      bestSeasons: uniqueText([...(base?.tactics?.bestSeasons || []), ...(next?.tactics?.bestSeasons || []), ...(base?.bestSeasons || []), ...(next?.bestSeasons || [])]),
      bestTimesOfDay: uniqueText([...(base?.tactics?.bestTimesOfDay || []), ...(next?.tactics?.bestTimesOfDay || []), ...(base?.bestTimesOfDay || []), ...(next?.bestTimesOfDay || [])]),
    },
  };
};

const buildSpeciesLookup = (items: any[]) => {
  const lookup = new Map<string, any>();
  const push = (item: any) => {
    if (!item) return;
    const canonical = canonicalizeSpecies(item);
    [
      canonical?.id,
      canonical?.slug,
      canonical?.commonName,
      canonical?.name,
      ...(Array.isArray(canonical?.aliases) ? canonical.aliases : []),
    ].forEach((value) => {
      const key = normalizeKey(value);
      if (key && !lookup.has(key)) lookup.set(key, canonical);
    });
  };
  items.forEach(push);
  return lookup;
};

const hoursAgoRounded = (ms: number | null) => {
  if (!ms || !Number.isFinite(ms)) return '—';
  const diff = Date.now() - ms;
  if (diff < 0) return '—';
  const h = Math.max(0, Math.round(diff / (60 * 60 * 1000)));
  if (h <= 0) return 'just now-ish';
  if (h === 1) return '~1 hour ago';
  return `~${h} hours ago`;
};

const timeAgoShort = (ms: number | null | undefined) => {
  if (!ms || !Number.isFinite(ms)) return 'just now';
  const diff = Math.max(0, Date.now() - ms);
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
};

const SignalBurst: React.FC<{ text: string }> = ({ text }) => (
  <div className="pointer-events-none fixed inset-x-0 top-24 z-[3000] flex justify-center px-4">
    <div className="animate-[floatUp_2.4s_ease-out_forwards] rounded-full border border-[#FFE08A]/80 bg-[linear-gradient(180deg,#FFF5C8,#FFE08A)] px-5 py-3 text-sm font-black uppercase tracking-[0.18em] text-[#5A3B00] shadow-[0_16px_36px_rgba(0,0,0,0.18)]">
      ✨ {text}
    </div>
  </div>
);



type RoddoPickerOption<T extends string> = {
  value: T;
  label: string;
  icon?: string;
  helper?: string;
};

type RoddoPickerSheetProps<T extends string> = {
  isOpen: boolean;
  title: string;
  subtitle?: string;
  value: T;
  options: Array<RoddoPickerOption<T>>;
  onSelect: (value: T) => void | boolean;
  onClose: () => void;
  isDarkMode: boolean;
  accent?: 'purple' | 'aqua';
};

function RoddoPickerSheet<T extends string>({
  isOpen,
  title,
  subtitle,
  value,
  options,
  onSelect,
  onClose,
  isDarkMode,
  accent = 'purple',
}: RoddoPickerSheetProps<T>) {
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen || typeof document === 'undefined') return null;

  const accentColor = accent === 'aqua'
    ? isDarkMode ? '#67e8f9' : '#1f9ca5'
    : isDarkMode ? '#c4b5fd' : '#7c5cff';

  return ReactDOM.createPortal(
    <div
      className="roddo-safe-overlay fixed inset-0 z-[10050] flex items-end justify-center px-3 min-[520px]:items-center"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={title}
      style={{
        background: isDarkMode ? 'rgba(9,13,24,0.72)' : 'rgba(32,24,14,0.30)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
      }}
    >
      <div
        className="roddo-safe-modal-frame relative flex w-full max-w-[380px] flex-col overflow-hidden rounded-[18px] border p-4 shadow-2xl"
        onClick={(event) => event.stopPropagation()}
        data-roddo-theme={isDarkMode ? 'dark' : 'light'}
        style={{
          background: isDarkMode
            ? '#0F1828'
            : '#FFFCF7',
          borderColor: isDarkMode ? 'rgba(255,255,255,0.14)' : 'rgba(93,67,39,0.14)',
          boxShadow: isDarkMode
            ? '0 28px 70px rgba(0,0,0,0.50)'
            : '0 24px 60px rgba(89,65,37,0.20)',
          color: isDarkMode ? '#F4F6FA' : '#2b2721',
        }}
      >
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background: 'transparent',
          }}
        />

        <div className="relative z-10 flex min-h-0 flex-1 flex-col">
          <div className="mb-3 flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div
                className="inline-flex items-center rounded-full border px-3 py-1 text-[10px] font-black uppercase tracking-[0.16em]"
                style={{
                  background: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.72)',
                  borderColor: isDarkMode ? 'rgba(148,166,201,0.08)' : 'rgba(93,67,39,0.10)',
                  color: accentColor,
                }}
              >
                Choose
              </div>
              <h3 className="mt-2 text-xl font-black tracking-tight">{title}</h3>
              {subtitle ? (
                <p className="mt-1 text-xs font-bold leading-5 opacity-70">{subtitle}</p>
              ) : null}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="grid h-10 w-10 shrink-0 place-items-center rounded-2xl border text-lg font-black transition active:translate-y-px"
              aria-label="Close picker"
              style={{
                background: isDarkMode ? 'rgba(255,255,255,0.055)' : 'rgba(255,255,255,0.74)',
                borderColor: isDarkMode ? 'rgba(255,255,255,0.12)' : 'rgba(93,67,39,0.12)',
                color: isDarkMode ? '#F4F6FA' : '#2b2721',
              }}
            >
              ×
            </button>
          </div>

          <div
            className="grid min-h-0 gap-2 overflow-y-auto overscroll-contain pr-1 [-webkit-overflow-scrolling:touch]"
            style={{ maxHeight: 'min(520px, calc(var(--roddo-visual-vh, 100dvh) - 220px))', scrollbarWidth: 'thin' }}
          >
            {options.map((option) => {
              const selected = option.value === value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    const shouldClose = onSelect(option.value);
                    if (shouldClose !== false) onClose();
                  }}
                  className="flex w-full items-center gap-3 rounded-[16px] border p-3 text-left transition active:translate-y-px"
                  style={{
                    background: selected
                      ? isDarkMode
                        ? 'rgba(112,87,232,0.15)'
                        : 'rgba(47,128,237,0.09)'
                      : isDarkMode
                        ? 'rgba(255,255,255,0.045)'
                        : 'rgba(255,255,255,0.70)',
                    borderColor: selected ? accentColor : isDarkMode ? 'rgba(148,166,201,0.08)' : 'rgba(93,67,39,0.10)',
                    boxShadow: selected
                      ? isDarkMode
                        ? '0 10px 24px rgba(0,0,0,0.20)'
                        : '0 10px 22px rgba(89,65,37,0.10)'
                      : 'none',
                  }}
                >
                  <span
                    className="grid h-10 w-10 shrink-0 place-items-center rounded-2xl border text-lg"
                    style={{
                      background: isDarkMode ? 'rgba(255,255,255,0.055)' : 'rgba(255,255,255,0.80)',
                      borderColor: selected ? accentColor : isDarkMode ? 'rgba(148,166,201,0.08)' : 'rgba(93,67,39,0.10)',
                    }}
                    aria-hidden="true"
                  >
                    {option.icon ?? '•'}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-black uppercase tracking-[0.10em]">{option.label}</span>
                    {option.helper ? <span className="mt-0.5 block text-[11px] font-bold opacity-65">{option.helper}</span> : null}
                  </span>
                  <span
                    className="grid h-7 w-7 shrink-0 place-items-center rounded-full border text-sm font-black"
                    style={{
                      background: selected ? accentColor : 'transparent',
                      borderColor: selected ? accentColor : isDarkMode ? 'rgba(255,255,255,0.20)' : 'rgba(93,67,39,0.20)',
                      color: selected ? '#fff' : isDarkMode ? 'rgba(174,184,201,0.70)' : 'rgba(43,39,33,0.52)',
                    }}
                    aria-hidden="true"
                  >
                    {selected ? '✓' : ''}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

const FishIQView: React.FC<FishIQViewProps> = ({
  userProfile,
  catchLog,
  onBack,
  onSelectSpecies,
  initialQuery = '',
  communityPattern = null,
  coords = null,
  mapContext = null,
  addToast,
  isDarkMode: isDarkModeProp,
}) => {
  const { t } = useTranslation();
  useEffect(() => {
    trackRoddoEvent('fishiq_opened', { source: mapContext?.source ? 'big_blue_context' : 'direct', account_type: (userProfile as any)?.accountType === 'guest' ? 'guest' : 'registered' });
  }, []);
  const modalScrollRef = useRef<HTMLDivElement | null>(null);
  const dailyTaskOpenRecordedRef = useRef(false);
  const [isDarkMode, setIsDarkMode] = useState<boolean>(() =>
    typeof isDarkModeProp === 'boolean' ? isDarkModeProp : resolveRoddoDarkMode(),
  );
  const [isScoutReadExpanded, setIsScoutReadExpanded] = useState(false);

  useEffect(() => {
    if (!userProfile?.uid || dailyTaskOpenRecordedRef.current) return;
    dailyTaskOpenRecordedRef.current = true;

    void completeDailyTaskAction('open_fishiq')
      .then((result) => {
        if (result?.completed && !result?.alreadyCompleted) {
          const shells = Math.max(0, Math.floor(Number(result.awardedShells || 0)));
          const title = String(result.dailyTask?.title || "Today's Cast task");
          addToast?.(`${title} complete — +${shells} Shell${shells === 1 ? '' : 's'}`, 'success');
        }
      })
      .catch((err) => console.warn('[DailyTask] open_fishiq skipped', err));
  }, [addToast, userProfile?.uid]);

  useEffect(() => {
    if (typeof isDarkModeProp === 'boolean') {
      setIsDarkMode(isDarkModeProp);
      return;
    }

    let lastTheme = resolveRoddoDarkMode();

    const syncTheme = () => {
      const nextTheme = resolveRoddoDarkMode();
      if (nextTheme !== lastTheme) {
        lastTheme = nextTheme;
        setIsDarkMode(nextTheme);
      }
    };

    setIsDarkMode(lastTheme);

    const media =
      typeof window !== 'undefined' && typeof window.matchMedia === 'function'
        ? window.matchMedia('(prefers-color-scheme: dark)')
        : null;

    const observer =
      typeof MutationObserver !== 'undefined'
        ? new MutationObserver(() => {
            window.requestAnimationFrame(syncTheme);
          })
        : null;

    if (observer) {
      observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['class', 'data-theme', 'style'],
      });
      if (document.body) {
        observer.observe(document.body, {
          attributes: true,
          attributeFilter: ['class', 'data-theme', 'style'],
        });
      }
    }

    const intervalId = window.setInterval(syncTheme, 220);

    window.addEventListener('rodo-theme-change', syncTheme as EventListener);
    window.addEventListener('themechange', syncTheme as EventListener);
    window.addEventListener('storage', syncTheme as EventListener);
    window.addEventListener('focus', syncTheme as EventListener);
    window.addEventListener('pageshow', syncTheme as EventListener);
    window.addEventListener('visibilitychange', syncTheme as EventListener);
    media?.addEventListener?.('change', syncTheme);

    return () => {
      observer?.disconnect();
      window.clearInterval(intervalId);
      window.removeEventListener('rodo-theme-change', syncTheme as EventListener);
      window.removeEventListener('themechange', syncTheme as EventListener);
      window.removeEventListener('storage', syncTheme as EventListener);
      window.removeEventListener('focus', syncTheme as EventListener);
      window.removeEventListener('pageshow', syncTheme as EventListener);
      window.removeEventListener('visibilitychange', syncTheme as EventListener);
      media?.removeEventListener?.('change', syncTheme);
    };
  }, [isDarkModeProp]);

  const ui = getRoddoUiTokens(isDarkMode);

  const panelStyle: React.CSSProperties = {
    background: isDarkMode ? '#0F1828' : '#FFFCF7',
    border: `1px solid ${ui.borderStrong}`,
    boxShadow: isDarkMode
      ? '0 14px 32px rgba(3,0,16,0.28), inset 0 1px 0 rgba(255,255,255,0.07)'
      : '0 10px 26px rgba(89,65,37,0.09), inset 0 1px 0 rgba(255,255,255,0.66)',
  };

  const surfaceStyle: React.CSSProperties = {
    background: isDarkMode ? '#101827' : '#F7F1E7',
    border: `1px solid ${ui.border}`,
    boxShadow: isDarkMode
      ? '0 8px 20px rgba(3,0,16,0.18), inset 0 1px 0 rgba(255,255,255,0.05)'
      : '0 6px 16px rgba(89,65,37,0.06), inset 0 1px 0 rgba(255,255,255,0.52)',
  };

  const resultsScrollWellStyle: React.CSSProperties = {
    background: isDarkMode
      ? '#10182A'
      : '#F8F2E9',
    border: `1px solid ${isDarkMode ? 'rgba(148,166,201,0.08)' : 'rgba(79,53,31,0.10)'}`,
    boxShadow: isDarkMode
      ? 'inset 0 1px 0 rgba(148,166,201,0.07), inset 0 0 22px rgba(7,4,18,0.18), 0 10px 24px rgba(0,0,0,0.16)'
      : 'inset 0 1px 0 rgba(255,255,255,0.72), inset 0 0 18px rgba(120,95,60,0.06), 0 10px 22px rgba(89,65,37,0.08)',
  };

  const textPrimary = { color: ui.textPrimary };
  const textSecondary = { color: isDarkMode ? '#AEB8C9' : '#564D43' };
  const textMuted = { color: isDarkMode ? '#7F8BA0' : '#70665B' };

  const [detail, setDetail] = useState<{
    isOpen: boolean;
    kind: 'species' | 'region';
    id: string | null;
  }>({
    isOpen: false,
    kind: 'species',
    id: null,
  });

  const [expandedRegionId, setExpandedRegionId] = useState<string | null>(null);
  const [selectedSpeciesSummaryId, setSelectedSpeciesSummaryId] = useState<string | null>(null);

  const {
    tab,
    setTab,
    query,
    setQuery,
    isLoading,
    species,
    regions,
    allSpecies,
    allRegions,
    waterRankedSpecies,
    savedSpeciesIds,
    savedRegionIds,
    toggleSpeciesSaved,
    toggleRegionSaved,
  } = useFishIQ({
    initialTab: 'species',
    initialQuery,
    waterTypeBias: (() => {
      const raw = String(
        mapContext?.waterType ||
          mapContext?.waterKind ||
          mapContext?.waterName ||
          communityPattern?.waterType ||
          communityPattern?.waterName ||
          '',
      ).toLowerCase();
      if (!raw) return null;
      // Freshwater/inland wins before broad coastal words so FishIQ does not
      // show coastal species for inland viewed areas in coastal states.
      if (/river|creek|stream|moving|waterway/.test(raw)) return 'river';
      if (/lake|reservoir|pond|freshwater|fresh water|inland|canal/.test(raw)) return 'lake';
      if (/ocean|sea|salt|coast|inshore|bay|harbor|harbour|estuary|marsh|surf|beach/.test(raw)) return 'inshore';
      if (/reservoir/.test(raw)) return 'reservoir';
      if (/pond/.test(raw)) return 'pond';
      if (/canal/.test(raw)) return 'canal';
      if (/lake|freshwater|fresh|local water/.test(raw)) return 'lake';
      return null;
    })(),
  });

  const tabOptions: Array<{ key: FishIQTab; label: string }> = [
    { key: 'species', label: 'Species' },
    { key: 'regions', label: 'Regions' },
    { key: 'saved', label: 'Saved' },
  ];

  const activeTabLabel = tabOptions.find((item) => item.key === tab)?.label ?? 'Species';
  const [aquaticFilter, setAquaticFilter] = useState<AquaticFilter>('all');
  const [waterTypeFilter, setWaterTypeFilter] = useState<WaterTypeFilter>('all');
  const [studyRegionId, setStudyRegionId] = useState<FishIQStudyRegionId>(() => {
    if (typeof window === 'undefined') return 'around';
    try {
      return String(window.localStorage.getItem(FISHIQ_STUDY_REGION_STORAGE_KEY) || 'around');
    } catch {
      return 'around';
    }
  });

  const [pickerSheet, setPickerSheet] = useState<null | 'tab' | 'aquatic' | 'water' | 'region'>(null);
  const [showAllStudyRegions, setShowAllStudyRegions] = useState(false);

  const aquaticFilterOptions: Array<RoddoPickerOption<AquaticFilter>> = [
    { value: 'all', label: 'All', icon: '🌊', helper: 'Fish and shells' },
    { value: 'fish', label: 'Fish', icon: '🐟', helper: 'Finfish only' },
    { value: 'shellfish', label: 'Shells', icon: '🦀', helper: 'Shellfish and crustaceans' },
  ];
  const waterTypeOptions: Array<RoddoPickerOption<WaterTypeFilter>> = [
    { value: 'all', label: 'Any water', icon: '🌊', helper: 'No water filter' },
    { value: 'freshwater', label: 'Fresh', icon: '🏞️', helper: 'Lakes, ponds, rivers' },
    { value: 'saltwater', label: 'Salt', icon: '🌊', helper: 'Coast, surf, offshore' },
    { value: 'brackish', label: 'Brackish', icon: '🌿', helper: 'Estuary and mixed water' },
  ];
  const tabPickerOptions: Array<RoddoPickerOption<FishIQTab>> = tabOptions.map((option) => ({
    value: option.key,
    label: option.label,
    icon: option.key === 'species' ? '🐟' : option.key === 'regions' ? '🗺️' : '⭐',
    helper: option.key === 'species' ? 'Pick a target' : option.key === 'regions' ? 'Browse area guides' : 'Saved reads',
  }));

  const aquaticFilterLabel = aquaticFilterOptions.find((option) => option.value === aquaticFilter)?.label ?? 'All';
  const waterTypeFilterLabel = waterTypeOptions.find((option) => option.value === waterTypeFilter)?.label ?? 'Any water';


  const [signalsLoading, setSignalsLoading] = useState(false);
  const [signalsError, setSignalsError] = useState<string | null>(null);
  const [signalsGeo5, setSignalsGeo5] = useState<string | null>(null);
  const [signals, setSignals] = useState<FishIQSignal[]>([]);
  const [signalsGated, setSignalsGated] = useState(false);
  const [signalDelta, setSignalDelta] = useState<number | null>(null);
  const [pendingContribution, setPendingContribution] = useState<PendingCatchFeedback | null>(null);
  const [burstText, setBurstText] = useState<string | null>(null);
  const [signalsExpanded, setSignalsExpanded] = useState(false);

  const [anchor, setAnchor] = useState<{
    lat: number;
    lon: number;
    source: string;
    usedFallback: boolean;
    ts: number;
  } | null>(null);

  const fishiqCatalogSeed = useMemo(() => (Array.isArray(FISHIQ_SPECIES_SEED) ? FISHIQ_SPECIES_SEED : []), []);

  const fishiqLocationContext = useMemo(() => {
    const mapCoords = mapContext?.coords || null;
    const mapPoint = mapCoords
      ? { lat: Number(mapCoords.lat), lon: Number(mapCoords.lon) }
      : null;
    const sharedPoint = coords
      ? { lat: Number(coords.lat), lon: Number(coords.lon) }
      : null;

    // Search-here map context wins over broader GPS/map coords.
    // Otherwise FishIQ opens correctly but ranks fish for the previous anchor.
    const bestCoords =
      mapPoint && Number.isFinite(mapPoint.lat) && Number.isFinite(mapPoint.lon)
        ? mapPoint
        : sharedPoint && Number.isFinite(sharedPoint.lat) && Number.isFinite(sharedPoint.lon)
          ? sharedPoint
          : null;

    return buildFishIQLocationContext({
      lat: bestCoords?.lat ?? null,
      lon: bestCoords?.lon ?? null,
      waterName: mapContext?.waterName || communityPattern?.waterName || null,
      waterType: mapContext?.waterType || mapContext?.waterKind || communityPattern?.waterType || null,
      waterKind: mapContext?.waterKind || mapContext?.waterType || communityPattern?.waterType || null,
      regionHint: mapContext?.regionHint || communityPattern?.regionKey || null,
      source: mapContext?.source || (communityPattern ? 'community-pattern' : bestCoords ? 'coords' : 'catalog'),
    });
  }, [coords, mapContext, communityPattern]);

  const detectedStudyProfiles = useMemo(
    () => getFishIQRegionalProfilesForContext({
      lat: fishiqLocationContext.lat,
      lon: fishiqLocationContext.lon,
      waterName: fishiqLocationContext.waterName,
      waterType: fishiqLocationContext.waterType,
      waterKind: fishiqLocationContext.waterKind,
      regionHint: fishiqLocationContext.regionHint,
    }),
    [fishiqLocationContext],
  );

  const detectedStudyProfile = useMemo(
    () => detectedStudyProfiles.find((profile) => !String(profile.id).startsWith('generic_')) || detectedStudyProfiles[0] || null,
    [detectedStudyProfiles],
  );

  const activeStudyProfile = useMemo(() => {
    if (studyRegionId === 'all') return null;
    if (studyRegionId === 'around') return detectedStudyProfile;
    return FISHIQ_REGIONAL_PROFILES.find((profile) => profile.id === studyRegionId) || null;
  }, [studyRegionId, detectedStudyProfile]);

  const studyRegionLabel = studyRegionId === 'all'
    ? 'All species'
    : activeStudyProfile?.label || (studyRegionId === 'around' ? 'Around me' : 'Selected region');

  const studyRegionOptions: Array<RoddoPickerOption<string>> = useMemo(() => {
    const allProfiles = FISHIQ_REGIONAL_PROFILES
      .filter((profile) => !String(profile.id).startsWith('generic_'));

    const detectedIds = new Set(
      detectedStudyProfiles
        .filter((profile) => !String(profile.id).startsWith('generic_'))
        .map((profile) => String(profile.id)),
    );

    // Keep the first-open picker intentionally tiny: Around Me + only the
    // profiles that actually matched the current location/context. If the user
    // has manually selected another region, keep that one visible too.
    const compactProfileIds = new Set<string>(detectedIds);
    if (studyRegionId !== 'around' && studyRegionId !== 'all') compactProfileIds.add(String(studyRegionId));

    const compactProfiles = allProfiles
      .filter((profile) => compactProfileIds.has(String(profile.id)))
      .slice(0, 4);

    const aroundOption: RoddoPickerOption<string> = {
      value: 'around',
      label: detectedStudyProfile ? `Around me — ${detectedStudyProfile.label}` : 'Around me',
      icon: '📍',
      helper: detectedStudyProfile ? 'Uses your current/cached region' : 'Uses local region when available',
    };

    if (!showAllStudyRegions) {
      return [
        aroundOption,
        ...compactProfiles
          .filter((profile) => String(profile.id) !== String(detectedStudyProfile?.id || ''))
          .map((profile) => ({
            value: String(profile.id),
            label: profile.label,
            icon: '🗺️',
            helper: 'Nearby / relevant study region',
          })),
        {
          value: '__explore_regions__',
          label: 'Explore all regions',
          icon: '🌎',
          helper: 'Open the full regional library',
        },
      ];
    }

    return [
      aroundOption,
      { value: 'all', label: 'All species', icon: '🌎', helper: 'Browse the full global library' },
      ...allProfiles.map((profile) => ({
        value: String(profile.id),
        label: profile.label,
        icon: '🗺️',
        helper: 'Static regional study catalog',
      })),
    ];
  }, [detectedStudyProfile, detectedStudyProfiles, showAllStudyRegions, studyRegionId]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(FISHIQ_STUDY_REGION_STORAGE_KEY, String(studyRegionId));
    } catch {}
  }, [studyRegionId]);

  const seedSpeciesLookup = useMemo(() => buildSpeciesLookup(fishiqCatalogSeed), [fishiqCatalogSeed]);

  const enrichSpecies = useMemo(() => {
    return (item: any) => {
      const keys = [
        item?.id,
        item?.slug,
        item?.commonName,
        item?.name,
        ...(Array.isArray(item?.aliases) ? item.aliases : []),
      ];
      const canonical = keys
        .map((value) => seedSpeciesLookup.get(normalizeKey(value)))
        .find(Boolean) ?? null;

      const merged = mergeSpeciesData(item, canonical);
      if (!merged) return null;

      const matchingSignal = getSpeciesSignal(merged, signals);
      const viewerCatchCount = countViewerSpeciesCatches(merged, catchLog);
      const signalReads = buildSupportingSignalReads(matchingSignal);
      const primarySignalRead = buildPrimarySignalRead(matchingSignal, viewerCatchCount);
      const existingObservationCount = Number(merged?.__fishiq?.observationCount ?? merged?.observationCount ?? 0);
      const signalObservationCount = Number(matchingSignal?.count24h ?? 0);
      const observationCount = Math.max(existingObservationCount, signalObservationCount);

      const confidenceLabel = primarySignalRead
        ? viewerCatchCount > 0
          ? 'Based on your recent catches'
          : signalObservationCount > 0
            ? 'Local signal is present'
            : String(merged?.__fishiq?.confidenceLabel ?? '').trim()
        : String(merged?.__fishiq?.confidenceLabel ?? '').trim();

      // Trust polish: when privacy-safe local aggregate evidence exists, show it
      // before catalog/profile defaults. Catalog data still remains the fallback.
      const nextBestBaits = mergeUniqueText(
        matchingSignal?.topLures,
        merged?.bestBaits,
        merged?.tactics?.bestBaits,
      ).slice(0, 4);

      const nextBestTechniques = mergeUniqueText(
        matchingSignal?.topTechniques,
        merged?.bestTechniques,
        merged?.tactics?.bestTechniques,
      ).slice(0, 4);

      const nextHabitats = mergeUniqueText(
        matchingSignal?.topWaterTypes,
        [matchingSignal?.waterTypeKey, matchingSignal?.waterKey],
        merged?.habitats,
        merged?.waterTypes,
      ).slice(0, 4);

      return {
        ...merged,
        bestBaits: nextBestBaits,
        bestTechniques: nextBestTechniques,
        habitats: nextHabitats,
        waterTypes: nextHabitats,
        topWaterTypes: mergeUniqueText(merged?.topWaterTypes, matchingSignal?.topWaterTypes).slice(0, 4),
        waterTypeKey: matchingSignal?.waterTypeKey ?? merged?.waterTypeKey,
        waterName: matchingSignal?.waterKey ? prettyKey(matchingSignal.waterKey) : merged?.waterName,
        viewerCatchCount,
        __fishiq: {
          ...(merged?.__fishiq || {}),
          observationCount,
          confidenceLabel: confidenceLabel || (observationCount > 0 ? 'Local signal is present' : 'Species guide'),
          primarySignal: primarySignalRead,
          supportingReads: signalReads,
          localReadTitle:
            viewerCatchCount > 0
              ? 'Based on your recent catches'
              : signalObservationCount > 0
                ? 'Local signal is present'
                : 'No local signal yet',
          localReadBody:
            viewerCatchCount > 0
              ? `Roddo found ${viewerCatchCount} ${viewerCatchCount === 1 ? 'recent catch' : 'recent catches'} from your log shaping this read.`
              : signalObservationCount > 0
                ? `Nearby shared signal is shown as field context layered over the documented species profile.`
                : 'Roddo is leaning on species behavior and water context first, not recent local proof.',
          signalMeta: matchingSignal
            ? {
                count24h: matchingSignal.count24h,
                uniqueUsers24h: matchingSignal.uniqueUsers24h,
                waterKey: matchingSignal.waterKey,
                waterTypeKey: matchingSignal.waterTypeKey,
                lastSeenAt: matchingSignal.lastSeenAt,
              }
            : undefined,
        },
      };
    };
  }, [seedSpeciesLookup, signals, catchLog]);

  const sortSpeciesAlphabetically = (items: any[]) =>
    [...items].sort((a: any, b: any) => {
      const aName = String(a?.commonName || a?.name || a?.id || '').trim();
      const bName = String(b?.commonName || b?.name || b?.id || '').trim();
      return aName.localeCompare(bName, undefined, { sensitivity: 'base', numeric: true });
    });

  const dedupeSpeciesCatalog = (items: any[]) =>
    dedupeFishIQSpeciesForPresentation(items, {
      query,
      suppressLocalGroups: true,
    });

  const studyScopedGlobalSpecies = useMemo(() => {
    const source = Array.isArray(allSpecies) ? allSpecies : [];
    if (studyRegionId === 'all') return source;
    return filterFishIQSpeciesToProfile(source, activeStudyProfile);
  }, [allSpecies, studyRegionId, activeStudyProfile]);

  const applySpeciesBrowseFilters = (items: any[]) =>
    items
      .filter((item: any) => speciesMatchesLocalQuery(item, query))
      .map((item) => enrichSpecies(item))
      .filter(Boolean)
      .filter((item: any) => {
        if (aquaticFilter === 'all') return true;
        const kind = String(item?.aquaticKind || 'finfish').toLowerCase();
        if (aquaticFilter === 'fish') return kind === 'finfish';
        return ['crustacean', 'mollusk', 'cephalopod', 'echinoderm', 'plant'].includes(kind);
      })
      .filter((item: any) => speciesMatchesWaterTypeFilter(item, waterTypeFilter));

  // The visible Species/Saved rolodex is always a predictable A-Z library.
  // Local FishIQ ranking is kept separate below for Today's Read / scout metrics.
  const speciesList = useMemo(() => {
    if (tab === 'regions') return [];

    const globalSource = Array.isArray(allSpecies) ? allSpecies : [];
    const savedMode = tab === 'saved';
    const baseList = savedMode
      ? globalSource.filter((item) => isFishIQItemSaved(item, savedSpeciesIds))
      : studyScopedGlobalSpecies;

    const filtered = applySpeciesBrowseFilters(baseList) as any[];
    return sortSpeciesAlphabetically(dedupeSpeciesCatalog(filtered));
  }, [allSpecies, studyScopedGlobalSpecies, query, tab, enrichSpecies, aquaticFilter, waterTypeFilter, savedSpeciesIds]);

  const speciesStudySections = useMemo(() => {
    const fallback = [{ key: 'all', title: '', helper: '', species: speciesList }];
    if (tab !== 'species' || studyRegionId === 'all' || !activeStudyProfile || query.trim()) return fallback;

    const featured: any[] = [];
    const regional: any[] = [];

    for (const item of speciesList) {
      const rule = getFishIQStudyRuleForSpecies(item, activeStudyProfile);
      if (isFishIQFeaturedStudyRule(rule)) featured.push(item);
      else regional.push(item);
    }

    const sections = [
      {
        key: 'featured',
        title: 'Featured in this region',
        helper: 'Strong and common regional study matches',
        species: featured,
      },
      {
        key: 'regional',
        title: 'More regional species',
        helper: 'Habitat, seasonal, and regional guide species',
        species: regional,
      },
    ].filter((section) => section.species.length > 0);

    return sections.length ? sections : fallback;
  }, [speciesList, tab, studyRegionId, activeStudyProfile, query]);

  // Keep the scouting/read intelligence relevance-ranked and water-aware.
  // This list is intentionally NOT used to order the visible catalog cards.
  const fishiqScoutSpeciesList = useMemo(() => {
    if (tab === 'regions') return [];

    const waterSourceRaw = Array.isArray(waterRankedSpecies) ? waterRankedSpecies : [];
    const visibleSourceRaw = Array.isArray(species) ? species : [];
    const globalSource = studyScopedGlobalSpecies;
    const waterSource = studyRegionId === 'all' ? waterSourceRaw : filterFishIQSpeciesToProfile(waterSourceRaw, activeStudyProfile);
    const visibleSource = studyRegionId === 'all' ? visibleSourceRaw : filterFishIQSpeciesToProfile(visibleSourceRaw, activeStudyProfile);
    const areaSource = waterSource.length >= 8
      ? waterSource
      : visibleSource.length >= 8
        ? visibleSource
        : globalSource;

    const enriched = areaSource
      .map((item) => enrichSpecies(item))
      .filter(Boolean);

    return rankFishIQSpeciesForLocation(enriched as any[], fishiqLocationContext, {
      signals,
      catchLog,
      query: '',
    });
  }, [species, waterRankedSpecies, studyScopedGlobalSpecies, studyRegionId, activeStudyProfile, tab, enrichSpecies, fishiqLocationContext, signals, catchLog]);

  const selectedSpeciesSummary = useMemo(() => {
    if (!selectedSpeciesSummaryId) return null;
    const key = String(selectedSpeciesSummaryId).trim().toLowerCase();
    return speciesList.find((item: any) => {
      return [item?.id, item?.slug, item?.commonName, item?.name]
        .map((value) => String(value ?? '').trim().toLowerCase())
        .filter(Boolean)
        .includes(key);
    }) ?? null;
  }, [selectedSpeciesSummaryId, speciesList]);

  useEffect(() => {
    if (!selectedSpeciesSummaryId) return;
    if (tab === 'regions' || speciesList.length === 0 || !selectedSpeciesSummary) {
      setSelectedSpeciesSummaryId(null);
    }
  }, [selectedSpeciesSummaryId, selectedSpeciesSummary, speciesList.length, tab]);

  const fishiqRankSummary = useMemo(() => getFishIQRankBucketSummary(fishiqScoutSpeciesList), [fishiqScoutSpeciesList]);
  const fishiqAreaReadGroups = useMemo(() => buildFishIQAreaReadGroups(fishiqScoutSpeciesList.slice(0, 16)), [fishiqScoutSpeciesList]);

  const fishiqAreaReasonChips = useMemo(() => buildFishIQAreaReasonChips(fishiqScoutSpeciesList.slice(0, 16)), [fishiqScoutSpeciesList]);

  const fishiqReadDebug = useMemo(() => {
    const areaRead = buildFishIQAreaRead({
      lat: Number(fishiqLocationContext.lat || 0),
      lon: Number(fishiqLocationContext.lon || 0),
      waterName: fishiqLocationContext.waterName || null,
      waterType: fishiqLocationContext.waterType || null,
      waterKind: fishiqLocationContext.waterKind || null,
      regionHint: fishiqLocationContext.regionHint || null,
      source: 'fishiq-view',
      species: fishiqScoutSpeciesList.slice(0, 16),
      signals,
      catchLog,
    });

    const label = areaRead.confidenceLabel || 'Limited local data';
    const confidence = label === 'Strong local match' ? 'high' : label === 'Likely match' ? 'medium' : 'low';
    return {
      water: buildFishIQWaterDebug({
        lat: fishiqLocationContext.lat,
        lon: fishiqLocationContext.lon,
        waterName: fishiqLocationContext.waterName,
        waterType: fishiqLocationContext.waterType,
        waterKind: fishiqLocationContext.waterKind,
        regionHint: fishiqLocationContext.regionHint,
        region: fishiqLocationContext.regionHint,
        selectedSpeciesCount: fishiqScoutSpeciesList.length,
        topRankingReasons: fishiqAreaReasonChips.slice(0, 5),
      }),
      areaRead,
      confidence: {
        confidence,
        label,
        reasons: areaRead.whyThisRead || [],
        waterContextReason: areaRead.evidence?.hasConfirmedWater ? 'water context resolved' : 'water context pending',
        fallbackUsed: areaRead.evidence?.fallbackOnly === true,
      },
    };
  }, [catchLog, fishiqAreaReasonChips, fishiqLocationContext, signals, fishiqScoutSpeciesList]);


  const regionList = useMemo(() => {
    const launchGuideCards = FISHIQ_LAUNCH_REGION_FILTERS.map(buildFishIQLaunchRegionCard);
    const q = query.trim().toLowerCase();

    const matchesQuery = (region: any) => {
      if (!q) return true;
      const launchRegion = getFishIQLaunchRegion(region);
      const searchable = [
        getFishIQRegionText(region),
        launchRegion?.key,
        launchRegion?.label,
        launchRegion?.shortLabel,
        launchRegion?.group,
        ...(launchRegion?.aliases || []),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return searchable.includes(q) || regionTextHasAlias(searchable, q);
    };

    const dedupeByLaunchKey = (items: FishIQRegion[]) => {
      const seen = new Set<string>();
      const out: FishIQRegion[] = [];

      for (const region of items) {
        const launchRegion = getFishIQLaunchRegion(region);
        const key = launchRegion?.key || String((region as any)?.id || (region as any)?.slug || (region as any)?.name || '').toLowerCase();
        if (!key || seen.has(key)) continue;
        seen.add(key);
        out.push(region);
      }

      return out;
    };

    // Regions tab should be a stable curated selector. Raw region tags from the
    // species catalog are intentionally not allowed to hide the launch cards.
    const curatedCards = dedupeByLaunchKey(launchGuideCards).filter(matchesQuery);

    if (tab === 'saved') {
      return curatedCards.filter((region) => isFishIQItemSaved(region, savedRegionIds));
    }

    if (tab !== 'regions') return [];

    return curatedCards;
  }, [tab, query, savedRegionIds]);

  const resolveSpeciesById = useMemo(() => {
    const lookup = new Map<string, any>();
    const push = (item: any) => {
      const enriched = enrichSpecies(item);
      if (!enriched) return;
      [
        enriched?.id,
        enriched?.slug,
        enriched?.commonName,
        enriched?.name,
        ...(Array.isArray(enriched?.aliases) ? enriched.aliases : []),
      ].forEach((value) => {
        const key = normalizeKey(value);
        if (key && !lookup.has(key)) lookup.set(key, enriched);
      });
    };
    fishiqCatalogSeed.forEach(push);
    (Array.isArray(allSpecies) ? allSpecies : []).forEach(push);
    (Array.isArray(speciesList) ? speciesList : []).forEach(push);
    return lookup;
  }, [allSpecies, speciesList, enrichSpecies, fishiqCatalogSeed]);

  const resolveRegionById = useMemo(() => {
    const lookup = new Map<string, any>();
    const push = (item: any) => {
      if (!item) return;
      [item?.id, item?.slug, item?.name, item?.title].forEach((value) => {
        const key = String(value ?? '').trim().toLowerCase();
        if (key && !lookup.has(key)) lookup.set(key, item);
      });
    };
    (Array.isArray(allRegions) ? allRegions : []).forEach(push);
    (Array.isArray(regionList) ? regionList : []).forEach(push);
    return lookup;
  }, [allRegions, regionList]);

  const selectedSpecies = useMemo(() => {
    if (!detail.isOpen || detail.kind !== 'species' || !detail.id) return null;
    const match = resolveSpeciesById.get(normalizeKey(detail.id)) ?? null;
    const enriched = match ? enrichSpecies(match) : null;
    return enriched
      ? rankFishIQSpeciesForLocation([enriched], fishiqLocationContext, {
          signals,
          catchLog,
          query: query.trim(),
        })[0] || enriched
      : null;
  }, [detail, resolveSpeciesById, enrichSpecies, fishiqLocationContext, signals, catchLog, query]);

  const selectedRegion = useMemo(() => {
    if (!detail.isOpen || detail.kind !== 'region' || !detail.id) return null;
    return resolveRegionById.get(String(detail.id).trim().toLowerCase()) ?? null;
  }, [detail, resolveRegionById]);

  const openSpecies = (id: string) => {
    trackRoddoEvent('fishiq_species_opened', { source: 'fishiq' });
    setDetail({ isOpen: true, kind: 'species', id });
  };
  const openRegion = (id: string) => setDetail({ isOpen: true, kind: 'region', id });
  const closeDetail = () => setDetail((p) => ({ ...p, isOpen: false }));

  useEffect(() => {
    const pending = consumePendingCatchFeedback();
    if (pending) {
      setPendingContribution(pending);
      setBurstText('Water Read Updated');
    }
  }, []);

  useEffect(() => {
    const onFishIQEvent = (evt: Event) => {
      const custom = evt as CustomEvent<any>;
      const detail = custom?.detail || {};
      if (detail?.type === 'CATCH_LOGGED' || detail?.type === 'CONTRIBUTION_SHARED') {
        setPendingContribution({
          id: String(detail?.payload?.sourceId ?? `evt_${Date.now()}`),
          species: String(detail?.payload?.species ?? detail?.payload?.speciesName ?? ''),
          location: String(detail?.payload?.location ?? detail?.payload?.waterName ?? ''),
          createdAt: Number(detail?.createdAt ?? Date.now()),
        });
        setBurstText('Signal Added');
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('roddo:fishiq:event', onFishIQEvent as EventListener);
      return () => window.removeEventListener('roddo:fishiq:event', onFishIQEvent as EventListener);
    }

    return undefined;
  }, []);

  useEffect(() => {
    if (!burstText) return;
    const tmr = window.setTimeout(() => setBurstText(null), 2400);
    return () => window.clearTimeout(tmr);
  }, [burstText]);

  useEffect(() => {
    if (!detail.isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeDetail();
    };

    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const frame = requestAnimationFrame(() => {
      if (modalScrollRef.current) modalScrollRef.current.scrollTop = 0;
    });

    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [detail.isOpen]);

  const BROWSER_GEO_CACHE_KEY = 'roddo.browserGeo.lastKnown.v1';
  const SIGNALS_CACHE_KEY = 'roddo.fishiq.signalsNearMe.v1';
  const SIGNALS_CACHE_TTL_MS = 30 * 60 * 1000;
  const SIGNALS_STALE_FALLBACK_MS = 24 * 60 * 60 * 1000;

  const buildSignalsCacheKey = (lat: number, lon: number) => {
    const roundedLat = Math.round(lat * 1000) / 1000;
    const roundedLon = Math.round(lon * 1000) / 1000;
    return `${roundedLat.toFixed(3)},${roundedLon.toFixed(3)}`;
  };

  const readSignalsCache = (lat: number, lon: number) => {
    try {
      const raw = window.localStorage.getItem(SIGNALS_CACHE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return null;
      const key = buildSignalsCacheKey(lat, lon);
      if (parsed?.key !== key) return null;
      if (!Array.isArray(parsed?.signals)) return null;
      return parsed as {
        key: string;
        ts: number;
        geo5: string | null;
        gated: boolean;
        signals: FishIQSignal[];
      };
    } catch {
      return null;
    }
  };

  const writeSignalsCache = (lat: number, lon: number, payload: { geo5: string | null; gated: boolean; signals: FishIQSignal[] }) => {
    try {
      window.localStorage.setItem(
        SIGNALS_CACHE_KEY,
        JSON.stringify({
          key: buildSignalsCacheKey(lat, lon),
          ts: Date.now(),
          geo5: payload.geo5,
          gated: payload.gated,
          signals: payload.signals,
        }),
      );
    } catch {}
  };


  const requestBrowserLocation = (): Promise<{ lat: number; lon: number }> =>
    new Promise((resolve, reject) => {
      if (coords && Number.isFinite(Number(coords.lat)) && Number.isFinite(Number(coords.lon))) {
        resolve({ lat: Number(coords.lat), lon: Number(coords.lon) });
        return;
      }

      try {
        const raw = window.localStorage.getItem(BROWSER_GEO_CACHE_KEY);
        if (raw) {
          const parsed = JSON.parse(raw);
          const lat = Number(parsed?.lat);
          const lon = Number(parsed?.lng ?? parsed?.lon);
          if (Number.isFinite(lat) && Number.isFinite(lon)) {
            resolve({ lat, lon });
            return;
          }
        }
      } catch {}

      if (typeof navigator === 'undefined' || !navigator.geolocation) {
        reject(new Error('Geolocation not available'));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ lat: pos.coords.latitude, lon: pos.coords.longitude }),
        (err) => reject(err),
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 },
      );
    });

  const fetchSignalsNearMeWithCoords = async (
    nextCoords: { lat: number; lon: number },
    opts?: { showUsingMapToast?: boolean; forceRefresh?: boolean },
  ) => {
    if (!userProfile?.uid) {
      setSignalsError('Sign-in required.');
      return;
    }

    setSignalsError(null);
    setSignalsLoading(true);

    try {
      // Launch quiet pass: map-context use is visible in the FishIQ read; no routine toast needed.

      const lat = Number(nextCoords.lat);
      const lon = Number(nextCoords.lon);

      const cached = !opts?.forceRefresh ? readSignalsCache(lat, lon) : null;
      if (cached && Date.now() - Number(cached.ts || 0) <= SIGNALS_CACHE_TTL_MS) {
        setSignalsGeo5(String(cached.geo5 ?? ''));
        setSignalsGated(Boolean(cached.gated));
        setSignals(Array.isArray(cached.signals) ? cached.signals : []);

        if (cached.geo5) {
          const total = (Array.isArray(cached.signals) ? cached.signals : []).reduce((sum, item) => sum + Number(item.count24h || 0), 0);
          const deltaInfo = getSignalDelta(String(cached.geo5), total);
          setSignalDelta(deltaInfo.delta);
          setSignalSnapshot(String(cached.geo5), total);
        } else {
          setSignalDelta(null);
        }
        return;
      }

      const res = await fishIQGetSignalsNearMe({
        lat,
        lon,
        waterType: mapContext?.waterType || mapContext?.waterKind || communityPattern?.waterType || null,
        waterKind: mapContext?.waterKind || mapContext?.waterType || communityPattern?.waterType || null,
        waterName: mapContext?.waterName || communityPattern?.waterName || null,
        regionHint: mapContext?.regionHint || communityPattern?.regionKey || null,
        speciesName: query?.trim?.() || null,
        forceRefresh: Boolean(opts?.forceRefresh),
      });
      const out: any = (res as any)?.data ?? res;

      if (!out?.ok) throw new Error('Signals request failed');

      const geo5 = String(out.geo5 ?? '');
      setSignalsGeo5(geo5);
      setSignalsGated(Boolean(out.gated));

      const raw = Array.isArray(out.signals) ? (out.signals as any[]) : [];
      const mapped: FishIQSignal[] = raw
        .map((x: any) => ({
          id: String(x?.signalId ?? x?.id ?? ''),
          geo5: String(x?.geo5 ?? out.geo5 ?? ''),
          timeBucket: String(x?.timeBucket ?? 'unknown'),
          speciesKey: String(x?.speciesKey ?? 'unknown'),
          waterKey: String(x?.waterKey ?? 'unknown'),
          waterTypeKey: String(x?.waterTypeKey ?? x?.topWaterTypeKey ?? ''),
          count24h: Number(x?.count24h ?? 0),
          uniqueUsers24h: Number(x?.uniqueUsers24h ?? 0),
          lastSeenAt: (x?.lastSeenAtMs ?? x?.lastSeenAt ?? null) as any,
          topTechniques: Array.isArray(x?.topTechniques) ? x.topTechniques : [],
          topLures: Array.isArray(x?.topLures) ? x.topLures : [],
          topWaterTypes: inferWaterTypes(x, x?.waterKey, x?.waterTypeKey),
          activityScore: Number(x?.activityScore ?? 0),
          activityRank: Number(x?.activityRank ?? 0) || undefined,
          activityBand: String(x?.activityBand ?? ''),
          regulatoryFlags: Array.isArray(x?.regulatoryFlags) ? x.regulatoryFlags.map((flag: any) => String(flag)) : [],
        }))
        .filter((x) => !!x.id)
        .sort((a, b) => {
          if (Number(b.activityScore || 0) !== Number(a.activityScore || 0)) return Number(b.activityScore || 0) - Number(a.activityScore || 0);
          if (Number(b.count24h || 0) !== Number(a.count24h || 0)) return Number(b.count24h || 0) - Number(a.count24h || 0);
          if (Number(b.uniqueUsers24h || 0) !== Number(a.uniqueUsers24h || 0)) return Number(b.uniqueUsers24h || 0) - Number(a.uniqueUsers24h || 0);
          return Number(b.lastSeenAt || 0) - Number(a.lastSeenAt || 0);
        });

      setSignals(mapped);
      writeSignalsCache(lat, lon, { geo5: geo5 || null, gated: Boolean(out.gated), signals: mapped });

      if (geo5) {
        const total = mapped.reduce((sum, item) => sum + Number(item.count24h || 0), 0);
        const deltaInfo = getSignalDelta(geo5, total);
        setSignalDelta(deltaInfo.delta);
        setSignalSnapshot(geo5, total);
      } else {
        setSignalDelta(null);
      }
    } catch (e: any) {
      try {
        const lat = Number(nextCoords.lat);
        const lon = Number(nextCoords.lon);
        const cached = readSignalsCache(lat, lon);
        if (cached && Date.now() - Number(cached.ts || 0) <= SIGNALS_STALE_FALLBACK_MS) {
          setSignalsGeo5(String(cached.geo5 ?? ''));
          setSignalsGated(Boolean(cached.gated));
          setSignals(Array.isArray(cached.signals) ? cached.signals : []);
          // Launch quiet pass: stale cache fallback is reflected in the UI state.
          return;
        }
      } catch {}

      const msg =
        e?.code === 1 || /permission/i.test(String(e?.message))
          ? 'Location permission denied.'
          : e?.message
            ? String(e.message)
            : 'Could not load signals.';
      setSignalsError(msg);
      setSignalsGeo5(null);
      setSignals([]);
      setSignalDelta(null);
    } finally {
      setSignalsLoading(false);
    }
  };

  const fetchSignalsNearMe = async (opts?: { forceRefresh?: boolean }) => {
    const usedSharedCoords = !!(coords && Number.isFinite(Number(coords.lat)) && Number.isFinite(Number(coords.lon)));

    if (anchor && Number.isFinite(Number(anchor.lat)) && Number.isFinite(Number(anchor.lon))) {
      await fetchSignalsNearMeWithCoords({ lat: Number(anchor.lat), lon: Number(anchor.lon) }, {
        showUsingMapToast: usedSharedCoords,
        forceRefresh: !!opts?.forceRefresh,
      });
      return;
    }

    const freshCoords = await requestBrowserLocation();
    await fetchSignalsNearMeWithCoords(freshCoords, {
      showUsingMapToast: usedSharedCoords,
      forceRefresh: !!opts?.forceRefresh,
    });
  };

  useEffect(() => {
    const mapCoords = mapContext?.coords || null;
    const mapLat = Number(mapCoords?.lat);
    const mapLon = Number(mapCoords?.lon);

    if (Number.isFinite(mapLat) && Number.isFinite(mapLon)) {
      setAnchor((prev) => {
        if (
          prev &&
          Math.abs(prev.lat - mapLat) < 0.000001 &&
          Math.abs(prev.lon - mapLon) < 0.000001 &&
          prev.source === 'map_context'
        ) {
          return prev;
        }

        return {
          lat: mapLat,
          lon: mapLon,
          source: 'map_context',
          usedFallback: false,
          ts: Date.now(),
        };
      });
      return;
    }

    if (coords && Number.isFinite(Number(coords.lat)) && Number.isFinite(Number(coords.lon))) {
      setAnchor((prev) => {
        const lat = Number(coords.lat);
        const lon = Number(coords.lon);
        if (
          prev &&
          Math.abs(prev.lat - lat) < 0.000001 &&
          Math.abs(prev.lon - lon) < 0.000001 &&
          prev.source === 'map_coords'
        ) {
          return prev;
        }
        return {
          lat,
          lon,
          source: 'map_coords',
          usedFallback: false,
          ts: Date.now(),
        };
      });
      return;
    }

    try {
      const raw = window.localStorage.getItem(BROWSER_GEO_CACHE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      const lat = Number(parsed?.lat);
      const lon = Number(parsed?.lng ?? parsed?.lon);
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;
      setAnchor((prev) => prev ?? {
        lat,
        lon,
        source: 'cache_boot',
        usedFallback: true,
        ts: Number(parsed?.ts ?? Date.now()),
      });
    } catch {}
  }, [coords?.lat, coords?.lon, mapContext?.coords?.lat, mapContext?.coords?.lon]);

  useEffect(() => {
    const onAnchorReady = (evt: Event) => {
      const custom = evt as CustomEvent<any>;
      const next = custom?.detail || {};
      const lat = Number(next?.lat);
      const lon = Number(next?.lon ?? next?.lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;

      const nextAnchor = {
        lat,
        lon,
        source: String(next?.source || 'gps'),
        usedFallback: !!next?.usedFallback,
        ts: Number(next?.ts ?? Date.now()),
      };

      setAnchor((prev) => {
        if (!prev) return nextAnchor;

        const moved = Math.abs(prev.lat - nextAnchor.lat) > 0.000001 || Math.abs(prev.lon - nextAnchor.lon) > 0.000001;
        const upgraded = prev.usedFallback && !nextAnchor.usedFallback;
        const betterSource = prev.source !== 'gps' && nextAnchor.source === 'gps';

        if (upgraded || betterSource || moved) return nextAnchor;
        return prev;
      });
    };

    window.addEventListener('roddo:anchor:ready', onAnchorReady as EventListener);
    return () => window.removeEventListener('roddo:anchor:ready', onAnchorReady as EventListener);
  }, []);

  // Cost-control pass: opening FishIQ never triggers a network signal read.
  // We hydrate recent local signal data from cache only; the existing Refresh
  // control remains the explicit opt-in path for a live nearby read.
  useEffect(() => {
    if (!anchor) return;
    const cached = readSignalsCache(Number(anchor.lat), Number(anchor.lon));
    if (!cached || Date.now() - Number(cached.ts || 0) > SIGNALS_STALE_FALLBACK_MS) return;

    const cachedSignals = Array.isArray(cached.signals) ? cached.signals : [];
    setSignalsGeo5(String(cached.geo5 ?? ''));
    setSignalsGated(Boolean(cached.gated));
    setSignals(cachedSignals);

    if (cached.geo5) {
      const total = cachedSignals.reduce((sum: number, item: any) => sum + Number(item?.count24h || 0), 0);
      const deltaInfo = getSignalDelta(String(cached.geo5), total);
      setSignalDelta(deltaInfo.delta);
      setSignalSnapshot(String(cached.geo5), total);
    }
  }, [anchor?.lat, anchor?.lon]);

  return (
    <RoddoPageShell padding="none" allowParentScroll contentClassName="max-w-4xl px-0">
      <style>{roddoTactilePolishStyles}</style>
      {burstText ? <SignalBurst text={burstText} /> : null}

      <div className="roddo-personality-shell space-y-3.5 pt-3 sm:space-y-4 sm:pt-4" data-roddo-tour="fishiq-page" data-roddo-tactile data-roddo-theme={isDarkMode ? 'dark' : 'light'} style={{ background: ui.surfaceBg }}>
        <div className="roddo-fishiq-magic-card overflow-hidden rounded-[20px] p-3.5 sm:rounded-[32px] sm:p-5" style={panelStyle}>
          <div className="space-y-4">
            <div className="px-1 sm:px-1.5">
              <div className="flex items-center justify-between gap-3">
                <div
                  className="inline-flex min-w-0 items-center gap-2 rounded-full px-3.5 py-2 text-[10px] font-black uppercase tracking-[0.16em] sm:px-4 sm:py-2.5 sm:text-[11px]"
                  style={{
                    background: isDarkMode ? 'rgba(138,112,242,0.11)' : 'rgba(47,128,237,0.08)',
                    color: isDarkMode ? '#D8D0FF' : '#2469B2',
                    border: `1px solid ${isDarkMode ? 'rgba(112,87,232,0.24)' : 'rgba(47,128,237,0.20)'}`,
                    boxShadow: isDarkMode ? '0 10px 22px rgba(0,0,0,0.16)' : '0 10px 20px rgba(89,65,37,0.08)',
                  }}
                >
                  <span aria-hidden="true">🧭</span>
                  <span className="truncate">Today’s read</span>
                </div>

                <button
                  type="button"
                  onClick={onBack}
                  className="grid h-10 w-10 shrink-0 place-items-center rounded-[16px] transition hover:-translate-y-0.5 active:translate-y-px sm:h-11 sm:w-11 sm:rounded-[18px]"
                  style={{
                    background: isDarkMode ? 'rgba(148,166,201,0.07)' : 'rgba(255,255,255,0.84)',
                    color: isDarkMode ? '#F8FAFF' : '#2F3D45',
                    border: `1px solid ${ui.border}`,
                    boxShadow: isDarkMode ? '0 10px 22px rgba(0,0,0,0.20), inset 0 1px 0 rgba(148,166,201,0.08)' : '0 10px 20px rgba(89,65,37,0.10), inset 0 1px 0 rgba(255,255,255,0.82)',
                  }}
                  aria-label="Back"
                >
                  <span className="text-xl font-black leading-none">‹</span>
                </button>
              </div>
            </div>


            <div
              className="overflow-hidden rounded-[18px] p-3 sm:rounded-[20px] sm:p-4"
              style={{
                ...surfaceStyle,
                background: isDarkMode ? '#101827' : '#FFFCF7',
              }}
            >
              <button
                type="button"
                onClick={() => setIsScoutReadExpanded((value) => !value)}
                className="flex w-full items-start justify-between gap-2 rounded-[16px] text-left"
                aria-expanded={isScoutReadExpanded}
                aria-controls="fishiq-scout-read-details"
              >
                <div className="flex min-w-0 flex-1 items-start gap-2.5 min-[390px]:gap-3">
                  <div
                    className="grid h-10 w-10 shrink-0 place-items-center rounded-[16px] text-[21px] min-[390px]:h-11 min-[390px]:w-11 min-[390px]:rounded-[18px] min-[390px]:text-[23px]"
                    style={{
                      background: isDarkMode ? 'rgba(138,112,242,0.11)' : 'rgba(47,128,237,0.08)',
                      border: `1px solid ${isDarkMode ? 'rgba(112,87,232,0.24)' : 'rgba(47,128,237,0.18)'}`,
                      boxShadow: isDarkMode ? '0 12px 24px rgba(0,0,0,0.20)' : '0 12px 22px rgba(89,65,37,0.09)',
                    }}
                  >
                    🧭
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[9px] font-black uppercase tracking-[0.14em] min-[390px]:text-[10px] min-[390px]:tracking-[0.18em]" style={textMuted}>Water scout read</div>
                    <h2 className="mt-0.5 text-[20px] font-black leading-tight min-[390px]:text-[22px]" style={textPrimary}>
                      Worth trying
                    </h2>
                    <p className="mt-1 max-w-[560px] text-[11.5px] font-semibold leading-relaxed min-[390px]:text-[12.5px]" style={textSecondary}>
                      {fishiqLocationContext.waterName || fishiqLocationContext.waterType
                        ? `${cleanFishIQAreaWaterLabel(fishiqLocationContext.waterName) || prettyKey(fishiqLocationContext.waterType)} — pick a target, cast, log it.`
                        : 'Pick a target, cast, log it.'}
                    </p>
                  </div>
                </div>

                <div className="flex max-w-[104px] shrink-0 flex-col items-end gap-1.5">
                  <span
                    className="rounded-full px-2 py-1 text-[7.5px] font-black uppercase tracking-[0.08em] min-[390px]:px-2.5 min-[390px]:text-[8px] min-[390px]:tracking-[0.12em]"
                    style={{
                      color: fishiqReadDebug.confidence.confidence === 'high' ? '#146C43' : fishiqReadDebug.confidence.confidence === 'medium' ? '#8A5A00' : '#8A3A3A',
                      background: fishiqReadDebug.confidence.confidence === 'high'
                        ? 'rgba(122,232,184,0.20)'
                        : fishiqReadDebug.confidence.confidence === 'medium'
                          ? 'rgba(255,217,119,0.22)'
                          : 'rgba(255,150,150,0.16)',
                    }}
                  >
                    {fishiqReadDebug.confidence.confidence === 'high'
                      ? 'Strong local match'
                      : fishiqReadDebug.confidence.confidence === 'medium'
                        ? 'Likely match'
                        : fishiqReadDebug.confidence.label}
                  </span>
                  <span
                    className="text-[9px] font-black uppercase tracking-[0.10em] opacity-70"
                    style={{ color: ui.textMuted }}
                    aria-hidden="true"
                  >
                    Tap to {isScoutReadExpanded ? 'hide' : 'read'}
                  </span>
                </div>
              </button>

              <div className="mt-3 grid grid-cols-3 gap-1.5 text-center min-[390px]:gap-2">
                <div className="rounded-[16px] border px-2 py-2 min-[390px]:px-2.5" style={{ borderColor: ui.border, background: isDarkMode ? '#0F1828' : '#F8F2E9' }}>
                  <div className="text-[16px] font-black tabular-nums min-[390px]:text-[17px]" style={textPrimary}>{speciesList.length}</div>
                  <div className="mt-0.5 text-[7.5px] font-black uppercase tracking-[0.06em] min-[390px]:text-[8.5px] min-[390px]:tracking-[0.10em]" style={textMuted}>🎯 Target</div>
                </div>
                <div className="rounded-[16px] border px-2 py-2 min-[390px]:px-2.5" style={{ borderColor: ui.border, background: isDarkMode ? '#0F1828' : '#F8F2E9' }}>
                  <div className="text-[16px] font-black tabular-nums min-[390px]:text-[17px]" style={textPrimary}>{fishiqRankSummary.good_target + fishiqRankSummary.possible}</div>
                  <div className="mt-0.5 text-[7.5px] font-black uppercase tracking-[0.06em] min-[390px]:text-[8.5px] min-[390px]:tracking-[0.10em]" style={textMuted}>🌿 Fits</div>
                </div>
                <div className="rounded-[16px] border px-2 py-2 min-[390px]:px-2.5" style={{ borderColor: ui.border, background: isDarkMode ? '#0F1828' : '#F8F2E9' }}>
                  <div className="text-[16px] font-black tabular-nums min-[390px]:text-[17px]" style={textPrimary}>{fishiqRankSummary.seasonal}</div>
                  <div className="mt-0.5 text-[7.5px] font-black uppercase tracking-[0.06em] min-[390px]:text-[8.5px] min-[390px]:tracking-[0.10em]" style={textMuted}>🌙 Time</div>
                </div>
              </div>

              {isScoutReadExpanded ? (
                <div id="fishiq-scout-read-details" className="mt-3 grid gap-2">
                  {fishiqAreaReasonChips.length > 0 ? (
                    <div className="grid grid-cols-1 gap-1.5" aria-label="Area guide context">
                      {fishiqAreaReasonChips.slice(0, 3).map((reason, idx) => {
                        const labels = ['💧 Water', '🌱 Season', '🌙 Window'];
                        return (
                          <div
                            key={reason}
                            className="grid grid-cols-[82px,minmax(0,1fr)] items-center gap-2 rounded-[15px] border px-2.5 py-1.5 min-[390px]:grid-cols-[92px,minmax(0,1fr)] min-[390px]:px-3"
                            style={{
                              borderColor: isDarkMode ? 'rgba(148,166,201,0.08)' : 'rgba(125,190,214,0.22)',
                              background: isDarkMode ? 'rgba(255,255,255,0.055)' : 'rgba(255,255,255,0.72)',
                              color: ui.textSecondary,
                            }}
                          >
                            <span className="truncate text-[8.5px] font-black uppercase tracking-[0.08em] opacity-75 min-[390px]:tracking-[0.10em]">
                              {labels[idx] || 'Read'}
                            </span>
                            <span className="min-w-0 truncate text-right text-[9.5px] font-black uppercase tracking-[0.04em] min-[390px]:tracking-[0.06em]">
                              {reason}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  ) : null}

                  <div
                    className="rounded-[18px] border px-3 py-2.5 text-left"
                    style={{
                      borderColor: isDarkMode ? 'rgba(148,166,201,0.08)' : 'rgba(125,190,214,0.22)',
                      background: isDarkMode ? 'rgba(255,255,255,0.055)' : 'rgba(255,255,255,0.66)',
                      color: ui.textSecondary,
                    }}
                  >
                    <div className="text-[9px] font-black uppercase tracking-[0.16em]" style={textMuted}>Why this read?</div>
                    <p className="mt-1.5 text-[11.5px] font-semibold leading-relaxed" style={textSecondary}>
                      {fishiqReadDebug.confidence.reasons.length > 0
                        ? fishiqReadDebug.confidence.reasons.slice(0, 4).join('. ')
                        : fishiqReadDebug.confidence.waterContextReason}.
                    </p>
                  </div>
                </div>
              ) : null}
            </div>


            {communityPattern ? (
              <div
                className="rounded-[18px] px-4 py-3"
                style={{
                  ...surfaceStyle,
                  background: isDarkMode
                    ? '#0F1828'
                    : '#F8F2E9',
                }}
              >
                <div className="flex min-w-0 flex-wrap items-start justify-between gap-2 min-[390px]:gap-3">
                  <div className="min-w-0">
                    <div className="text-[11px] font-black uppercase tracking-[0.18em]" style={textMuted}>
                      Pattern from Baleen Bay
                    </div>
                    <div className="mt-1 text-[15px] font-black" style={textPrimary}>
                      {[communityPattern.species, communityPattern.timeBucket, communityPattern.technique, communityPattern.lureOrBait]
                        .filter(Boolean)
                        .join(' • ') || 'Community catch signal'}
                    </div>
                    <div className="mt-0.5 text-[12.5px]" style={textSecondary}>
                      Roddo is matching time, conditions, and technique to suggest a cleaner next hunch.
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {communityPattern.waterName ? <span className="rounded-full px-2.5 py-1 text-xs font-bold" style={{ background: isDarkMode ? 'rgba(148,166,201,0.07)' : 'rgba(255,255,255,0.72)', color: ui.textPrimary }}>💧 {communityPattern.waterName}</span> : null}
                    {(communityPattern.conditions || []).slice(0, 3).map((c) => (
                      <span key={c} className="rounded-full px-2.5 py-1 text-xs font-bold" style={{ background: isDarkMode ? 'rgba(148,166,201,0.07)' : 'rgba(255,255,255,0.72)', color: ui.textPrimary }}>🌦 {c}</span>
                    ))}
                    {communityPattern.technique ? <span className="rounded-full px-2.5 py-1 text-xs font-bold" style={{ background: isDarkMode ? 'rgba(148,166,201,0.07)' : 'rgba(255,255,255,0.72)', color: ui.textPrimary }}>🎣 {communityPattern.technique}</span> : null}
                  </div>
                </div>
              </div>
            ) : null}

            {pendingContribution ? (
              <div className="rounded-[18px] px-4 py-3" style={surfaceStyle}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="text-[11px] font-black uppercase tracking-[0.18em]" style={textMuted}>
                      Contribution recorded
                    </div>
                    <div className="mt-1 text-[15px] font-black" style={textPrimary}>
                      {pendingContribution.species || 'Catch added to Roddo'}
                    </div>
                    <div className="mt-1 text-sm" style={textSecondary}>
                      {pendingContribution.location
                        ? `Logged near ${pendingContribution.location} · ${timeAgoShort(pendingContribution.createdAt)}`
                        : 'Your catch was added to Roddo’s fishing memory.'}
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

          </div>
        </div>

        <div className="overflow-hidden rounded-[20px] p-3 sm:rounded-[20px] sm:p-4" style={panelStyle}>
          <div className="space-y-3.5">
            <div
              className="relative overflow-hidden rounded-[18px] p-3 sm:rounded-[20px] sm:p-3.5"
              style={{
                background: isDarkMode
                  ? 'rgba(18,26,43,0.96)'
                  : 'linear-gradient(145deg, rgba(255,255,255,0.96), rgba(250,245,238,0.90))',
                border: `1px solid ${isDarkMode ? 'rgba(148,166,201,0.08)' : 'rgba(207,184,151,0.30)'}`,
                boxShadow: isDarkMode
                  ? '0 18px 42px rgba(0,0,0,0.26), inset 0 1px 0 rgba(148,166,201,0.07)'
                  : '0 16px 34px rgba(89,65,37,0.10), inset 0 1px 0 rgba(255,255,255,0.78)',
              }}
            >

              <div className="relative flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div
                    className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.16em]"
                    style={{
                      background: isDarkMode ? 'rgba(138,112,242,0.11)' : 'rgba(47,128,237,0.08)',
                      color: isDarkMode ? '#D8D0FF' : '#2469B2',
                      border: `1px solid ${isDarkMode ? 'rgba(112,87,232,0.24)' : 'rgba(47,128,237,0.20)'}`,
                    }}
                  >
                    📚 Species study
                  </div>
                  <h2 className="mt-2 text-[20px] font-black leading-tight min-[390px]:text-[21px]" style={textPrimary}>
                    {tab === 'regions' ? 'Region guide' : tab === 'species' ? 'Study local species' : 'Saved species'}
                  </h2>
                </div>

                <div className="relative w-[118px] shrink-0 min-[390px]:w-[132px]">
                  <button
                    type="button"
                    onClick={() => setPickerSheet('tab')}
                    className="flex w-full items-center justify-between gap-2 rounded-full px-3 py-2.5 text-left text-[9px] font-black uppercase tracking-[0.08em] outline-none transition active:translate-y-px min-[390px]:px-4 min-[390px]:text-[10px] min-[390px]:tracking-[0.12em]"
                    style={{
                      background: isDarkMode
                        ? 'rgba(138,112,242,0.12)'
                        : 'rgba(47,128,237,0.08)',
                      border: `1.5px solid ${isDarkMode ? 'rgba(112,87,232,0.28)' : 'rgba(47,128,237,0.22)'}`,
                      color: isDarkMode ? '#FFFFFF' : '#2469B2',
                      boxShadow: isDarkMode ? '0 12px 24px rgba(0,0,0,0.22)' : '0 12px 22px rgba(89,65,37,0.09)',
                    }}
                    aria-label="Choose water read section"
                  >
                    <span className="min-w-0 truncate">{activeTabLabel}</span>
                    <span className="shrink-0 text-[10px] font-black" style={{ color: ui.textMuted }}>▾</span>
                  </button>
                </div>
              </div>

              <div className="relative mt-3 grid gap-2.5">
                <div className="relative">
                  <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={
                      tab === 'regions'
                        ? t('fishIQ.searchRegionPlaceholder', 'Search region or coast')
                        : t('fishIQ.searchPlaceholder', 'Search bass, bluegill, tuna...')
                    }
                    className="w-full rounded-[16px] px-4 py-3 pr-10 text-[13px] font-bold outline-none transition"
                    style={{
                      background: isDarkMode ? '#0A1120' : 'rgba(255,252,247,0.98)',
                      border: `1.5px solid ${ui.border}`,
                      color: isDarkMode ? '#F8FAFF' : '#2F2A24',
                      boxShadow: isDarkMode
                        ? 'inset 0 2px 0 rgba(255,255,255,0.06)'
                        : 'inset 0 2px 0 rgba(255,255,255,0.78), 0 10px 20px rgba(89,65,37,0.06)',
                    }}
                  />
                  <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-sm opacity-70">⌕</span>
                </div>

                {tab === 'species' ? (
                  <button
                    type="button"
                    onClick={() => { setShowAllStudyRegions(false); setPickerSheet('region'); }}
                    className="min-w-0 rounded-[16px] px-3.5 py-3 text-left outline-none transition active:translate-y-px"
                    style={{
                      background: isDarkMode
                        ? 'rgba(138,112,242,0.10)'
                        : 'rgba(47,128,237,0.07)',
                      border: `1.5px solid ${isDarkMode ? 'rgba(138,112,242,0.20)' : 'rgba(47,128,237,0.18)'}`,
                      color: ui.textPrimary,
                    }}
                    aria-label="Choose study region"
                  >
                    <span className="flex items-center justify-between gap-3">
                      <span className="min-w-0">
                        <span className="block truncate text-[11px] font-black uppercase tracking-[0.12em]">
                          {studyRegionId === 'all' ? '🌎 All species' : `📍 ${studyRegionLabel}`}
                        </span>
                        <span className="mt-0.5 block truncate text-[10px] font-bold" style={{ color: ui.textMuted }}>
                          {studyRegionId === 'all' ? 'Global study library' : 'Static study library · live data stays off'}
                        </span>
                      </span>
                      <span className="shrink-0 text-[11px] font-black" style={{ color: ui.textMuted }}>▾</span>
                    </span>
                  </button>
                ) : null}

                {tab !== 'regions' ? (
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setPickerSheet('aquatic')}
                      className="min-w-0 rounded-full px-2.5 py-2.5 text-left text-[8.5px] font-black uppercase tracking-[0.055em] outline-none transition active:translate-y-px min-[390px]:px-3 min-[390px]:text-[9.5px]"
                      style={{
                        background: aquaticFilter !== 'all'
                          ? isDarkMode
                            ? 'rgba(138,112,242,0.12)'
                            : 'rgba(47,128,237,0.08)'
                          : isDarkMode
                            ? 'rgba(255,255,255,0.055)'
                            : 'rgba(255,255,255,0.78)',
                        color: aquaticFilter !== 'all' ? (isDarkMode ? '#FFFFFF' : '#2469B2') : ui.textSecondary,
                        border: `1.5px solid ${aquaticFilter !== 'all' ? (isDarkMode ? 'rgba(167,139,250,0.34)' : 'rgba(139,108,255,0.24)') : ui.border}`,
                      }}
                      aria-label="Choose target type"
                    >
                      <span className="flex items-center justify-between gap-2">
                        <span className="min-w-0 truncate">{aquaticFilterLabel}</span>
                        <span className="shrink-0 text-[10px] font-black" style={{ color: ui.textMuted }}>▾</span>
                      </span>
                    </button>

                    <button
                      type="button"
                      onClick={() => setPickerSheet('water')}
                      className="min-w-0 rounded-full px-2.5 py-2.5 text-left text-[8.5px] font-black uppercase tracking-[0.055em] outline-none transition active:translate-y-px min-[390px]:px-3 min-[390px]:text-[9.5px]"
                      style={{
                        background: waterTypeFilter !== 'all'
                          ? isDarkMode
                            ? 'rgba(138,112,242,0.11)'
                            : 'rgba(47,128,237,0.08)'
                          : isDarkMode
                            ? 'rgba(255,255,255,0.055)'
                            : 'rgba(255,255,255,0.78)',
                        color: waterTypeFilter !== 'all' ? (isDarkMode ? '#FFFFFF' : '#2469B2') : ui.textSecondary,
                        border: `1.5px solid ${waterTypeFilter !== 'all' ? (isDarkMode ? 'rgba(103,232,249,0.28)' : 'rgba(80,180,180,0.24)') : ui.border}`,
                      }}
                      aria-label="Choose water type"
                    >
                      <span className="flex items-center justify-between gap-2">
                        <span className="min-w-0 truncate">{waterTypeFilterLabel}</span>
                        <span className="shrink-0 text-[10px] font-black" style={{ color: ui.textMuted }}>▾</span>
                      </span>
                    </button>
                  </div>
                ) : null}

                {tab !== 'regions' && (aquaticFilter !== 'all' || waterTypeFilter !== 'all') ? (
                  <div className="flex items-center justify-between gap-2 rounded-full px-3 py-2" style={{ background: isDarkMode ? 'rgba(255,255,255,0.045)' : 'rgba(255,255,255,0.64)', border: `1px solid ${ui.border}` }}>
                    <div className="min-w-0 truncate text-[10.5px] font-bold" style={{ color: ui.textSecondary }}>
                      {aquaticFilter === 'all' ? 'All' : aquaticFilter === 'fish' ? 'Fish' : 'Shells'}
                      {' · '}
                      {waterTypeFilter === 'all' ? 'Any water' : waterTypeFilter === 'freshwater' ? 'Fresh' : waterTypeFilter === 'saltwater' ? 'Salt' : waterTypeFilter}
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setAquaticFilter('all');
                        setWaterTypeFilter('all');
                      }}
                      className="shrink-0 rounded-full px-2.5 py-1 text-[9px] font-black uppercase tracking-[0.08em] transition hover:-translate-y-0.5 active:translate-y-px"
                      style={{
                        background: isDarkMode ? 'rgba(255,255,255,0.07)' : 'rgba(255,255,255,0.86)',
                        color: ui.textSecondary,
                        border: `1px solid ${ui.border}`,
                      }}
                    >
                      Reset
                    </button>
                  </div>
                ) : null}
              </div>
            </div>

            <div className="flex min-w-0 flex-wrap items-center justify-between gap-2">
              <div>
                <div className="text-[10px] font-black uppercase tracking-[0.20em]" style={textMuted}>
                  Study library
                </div>
                <h2 className="mt-0.5 text-[18px] font-black min-[390px]:text-[20px]" style={textPrimary}>
                  {tab === 'regions' ? '🗺️ Region guide' : tab === 'species' ? `📍 ${studyRegionLabel}` : '⭐ Saved species'}
                </h2>
              </div>
              <div
                className="rounded-full px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.12em]"
                style={{
                  background: isDarkMode ? 'rgba(148,166,201,0.07)' : 'rgba(255,255,255,0.86)',
                  color: ui.textSecondary,
                  border: `1px solid ${ui.border}`,
                }}
              >
                {tab === 'regions' ? `${regionList.length} regions` : `${speciesList.length} species`}
              </div>
            </div>

            <div
              className="rounded-[18px] p-2"
              style={{
                ...resultsScrollWellStyle,
                minHeight: 0,
              }}
            >
              <div
                className="pr-1 roddo-fishiq-results-list"
                style={{
                  maxHeight: 'none',
                  minHeight: 0,
                  overflowY: 'visible',
                  transition: 'max-height 180ms ease',
                  WebkitMaskImage: 'none',
                  maskImage: 'none',
                }}
              >
              {isLoading ? (
                <div className="rounded-[18px] p-4 text-sm" style={{ ...surfaceStyle, ...textSecondary }}>
                  Roddo is reading the water and building a cleaner target list…
                </div>
              ) : null}

              {!isLoading && tab !== 'regions' && speciesList.length === 0 ? (
                <div className="rounded-[18px] border p-4 text-sm" style={{ ...surfaceStyle, ...textSecondary, borderColor: isDarkMode ? 'rgba(255,255,255,0.12)' : 'rgba(91,67,47,0.12)' }}>
                  <div className="flex items-start gap-3">
                    <div className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl text-xl" style={{ background: isDarkMode ? 'rgba(56,189,248,0.16)' : 'rgba(186,230,253,0.72)' }}>🧭</div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-black" style={{ color: isDarkMode ? '#fff7e8' : '#334155' }}>
                        {query.trim() ? 'No species match in this view' : tab === 'saved' ? 'Build your target shortlist' : 'No regional species match yet'}
                      </div>
                      <p className="mt-1 text-xs font-bold leading-relaxed" style={textSecondary}>
                        {query.trim()
                          ? mapContext || communityPattern?.waterType || communityPattern?.waterName
                            ? `No strong local water match for “${query.trim()}” here. Move Big Blue over matching water or try a nearby lake, river, bay, or coast.`
                            : 'No matching species yet. Try a simpler species name or start from a nearby water.'
                          : tab === 'saved'
                            ? 'Tap the star on any species card to save your best bets. Your shortlist will live here for quick trip planning.'
                            : 'Open Big Blue, center the map over water, then come back for a smarter target list. Roddo works best when it knows the water.'}
                      </p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={onBack}
                          className="rounded-full px-3 py-2 text-xs font-black transition active:scale-95"
                          style={{ background: isDarkMode ? 'rgba(255,255,255,0.12)' : '#ffe08a', color: isDarkMode ? '#fff7e8' : '#3f3428' }}
                        >
                          🌊 Scout Big Blue
                        </button>
                        {tab !== 'saved' ? (
                          <button
                            type="button"
                            onClick={() => setTab('saved')}
                            className="rounded-full px-3 py-2 text-xs font-black transition active:scale-95"
                            style={{ background: isDarkMode ? 'rgba(148,166,201,0.07)' : 'rgba(255,255,255,0.78)', color: isDarkMode ? '#fff7e8' : '#475569' }}
                          >
                            ⭐ View saved
                          </button>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}

              {!isLoading && tab === 'regions' && regionList.length === 0 ? (
                <div className="rounded-[18px] p-4 text-sm" style={{ ...surfaceStyle, ...textSecondary }}>
                  {query.trim() ? 'No matching regions. Try a different name.' : 'No regions available yet.'}
                </div>
              ) : null}

              {!isLoading && tab !== 'regions' && speciesList.length > 0 ? (
                <div
                  data-roddo-tour="fishiq-species-list"
                  className="w-full max-w-full min-w-0 overflow-y-auto overscroll-contain pr-1 space-y-2.5 snap-y snap-mandatory rounded-[20px] [-webkit-overflow-scrolling:touch]"
                  style={{
                    maxHeight: 'calc((78px * 4) + (0.625rem * 3) + 10px)',
                    scrollbarWidth: 'thin',
                  }}
                >
                  {speciesStudySections.map((section) => (
                    <React.Fragment key={section.key}>
                      {section.title ? (
                        <div className="px-1 pb-0.5 pt-2 first:pt-0">
                          <div className="text-[11px] font-black uppercase tracking-[0.14em]" style={textPrimary}>
                            {section.title}
                          </div>
                          <div className="mt-0.5 text-[10px] font-bold" style={textMuted}>
                            {section.helper}
                          </div>
                        </div>
                      ) : null}
                      {section.species.map((s: any) => {
                        const id = String((s as any).id || (s as any).slug || (s as any).commonName || '');
                        const isSaved = isFishIQItemSaved(s, savedSpeciesIds);
                        const toggleKey = getFishIQSavedToggleKey(s, savedSpeciesIds);
                        const cardIndex = speciesList.indexOf(s);

                        return (
                      <div
                        key={id}
                        className="group relative w-full max-w-full min-w-0 rounded-[18px] p-[1.5px] roddo-result-reveal snap-start"
                        style={{
                          animationDelay: `${Math.min(360, 60 + (cardIndex * 45))}ms`,
                          background: isDarkMode
                            ? 'rgba(138,112,242,0.20)'
                            : 'rgba(47,128,237,0.16)',
                          boxShadow: isDarkMode
                            ? '0 18px 38px rgba(0,0,0,0.22)'
                            : '0 16px 34px rgba(89,65,37,0.10)',
                        }}
                      >
                        <div
                          className="pointer-events-none absolute left-5 right-16 top-0 h-[3px] rounded-full opacity-90"
                          style={{
                            background: isDarkMode
                              ? '#8A70F2'
                              : '#2F80ED',
                          }}
                        />
                        <div
                          className="relative rounded-[27px] transition duration-200 group-hover:-translate-y-0.5 group-hover:brightness-[1.02]"
                          style={{
                            background: isDarkMode ? 'rgba(18,15,44,0.20)' : 'rgba(255,255,255,0.18)',
                          }}
                        >
                          <FishIQSpeciesCard
                            species={s}
                            isDarkMode={isDarkMode}
                            isSaved={isSaved}
                            isActive={selectedSpeciesSummaryId === id}
                            onToggleSaved={() => toggleSpeciesSaved(toggleKey)}
                            onOpenSummary={() => setSelectedSpeciesSummaryId(id)}
                          />
                        </div>
                      </div>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </div>
              ) : null}

              {/* Summary card now renders as a viewport-centered portal below, not inline in the rolodex. */}

              {!isLoading && tab === 'regions' && regionList.length > 0 ? (
                <div className="space-y-2.5">
                  {regionList.map((r: FishIQRegion) => {
                    const id = String((r as any).id || (r as any).slug || (r as any).name || '');
                    const isSaved = isFishIQItemSaved(r, savedRegionIds);
                    const toggleKey = getFishIQSavedToggleKey(r, savedRegionIds);
                    const launchRegion = getFishIQLaunchRegion(r);
                    const topSpecies = Array.isArray((r as any).topSpecies) ? (r as any).topSpecies.slice(0, 5) : [];
                    const waterways = Array.isArray((r as any).waterways) ? (r as any).waterways.slice(0, 3) : [];
                    const starterMoves = Array.isArray((r as any).starterMoves) ? (r as any).starterMoves.slice(0, 3) : [];
                    const isExpanded = expandedRegionId === id;
                    const cardTitle = launchRegion?.shortLabel || getFishIQRegionDisplayName(r);

                    return (
                      <article
                        key={id}
                        className="block w-full rounded-[20px] p-3 text-left"
                        style={{
                          ...surfaceStyle,
                          border: `1px solid ${ui.border}`,
                          boxShadow: isDarkMode
                            ? '0 10px 20px rgba(0,0,0,0.14)'
                            : '0 10px 18px rgba(94,67,38,0.055)',
                        }}
                      >
                        <div className="flex items-start gap-2.5">
                          <button
                            type="button"
                            onClick={() => setExpandedRegionId((prev) => (prev === id ? null : id))}
                            className="grid h-10 w-10 shrink-0 place-items-center rounded-2xl text-[20px] transition active:scale-95"
                            style={{
                              background: isDarkMode
                                ? 'rgba(255,255,255,0.07)'
                                : 'rgba(255,255,255,0.76)',
                              border: `1px solid ${ui.border}`,
                            }}
                            aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${cardTitle}`}
                          >
                            {launchRegion?.emoji || '🗺️'}
                          </button>

                          <div className="min-w-0 flex-1">
                            <div className="flex min-w-0 items-start justify-between gap-2">
                              <button
                                type="button"
                                onClick={() => setExpandedRegionId((prev) => (prev === id ? null : id))}
                                className="min-w-0 flex-1 text-left transition active:scale-[0.99]"
                              >
                                <div className="truncate text-[15px] font-black" style={textPrimary}>
                                  {cardTitle}
                                </div>
                                {launchRegion?.group ? (
                                  <div className="mt-0.5 text-[9px] font-black uppercase tracking-[0.16em]" style={textMuted}>
                                    {launchRegion.group === 'Growth' ? 'Growth pack' : launchRegion.group === 'Pilot' ? 'Pilot area' : 'Catalog guide'}
                                  </div>
                                ) : null}
                              </button>

                              <div className="flex shrink-0 items-center gap-1.5">
                                <button
                                  type="button"
                                  onClick={() => setExpandedRegionId((prev) => (prev === id ? null : id))}
                                  className="grid h-8 w-8 place-items-center rounded-full text-[17px] font-black leading-none transition active:scale-95"
                                  style={{
                                    background: isDarkMode ? 'rgba(124,199,255,0.10)' : 'rgba(234,247,255,0.78)',
                                    color: isDarkMode ? '#DFF1FF' : '#0D5B85',
                                    border: `1px solid ${isDarkMode ? 'rgba(124,199,255,0.16)' : 'rgba(124,199,255,0.24)'}`,
                                  }}
                                  aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${cardTitle}`}
                                  title={isExpanded ? 'Collapse' : 'Expand'}
                                >
                                  {isExpanded ? '−' : '+'}
                                </button>

                                <button
                                  type="button"
                                  onClick={() => toggleRegionSaved(toggleKey)}
                                  className="grid h-8 w-8 place-items-center rounded-full text-[16px] font-black leading-none transition active:scale-95"
                                  style={{
                                    background: isSaved
                                      ? 'rgba(246,196,83,0.22)'
                                      : isDarkMode
                                        ? 'rgba(148,166,201,0.07)'
                                        : 'rgba(255,255,255,0.82)',
                                    color: isSaved
                                      ? isDarkMode
                                        ? '#FFE8A3'
                                        : '#6A5000'
                                      : isDarkMode
                                        ? '#EAEFFF'
                                        : '#6A6258',
                                    border: `1px solid ${ui.border}`,
                                  }}
                                  aria-label={isSaved ? `Unsave ${cardTitle}` : `Save ${cardTitle}`}
                                  title={isSaved ? 'Saved' : 'Save'}
                                >
                                  {isSaved ? '★' : '☆'}
                                </button>
                              </div>
                            </div>

                            {(r as any).summary ? (
                              <p className={isExpanded ? 'mt-2 text-[12px] font-semibold leading-relaxed' : 'mt-1.5 line-clamp-2 text-[11.5px] font-semibold leading-relaxed'} style={textSecondary}>
                                {(r as any).summary}
                              </p>
                            ) : null}

                            {!isExpanded && topSpecies.length ? (
                              <div className="mt-2 flex flex-wrap gap-1.5">
                                {topSpecies.slice(0, 3).map((name: string) => (
                                  <span
                                    key={name}
                                    className="rounded-full px-2 py-0.5 text-[9px] font-black"
                                    style={{
                                      background: isDarkMode ? 'rgba(45,212,191,0.08)' : 'rgba(204,251,241,0.44)',
                                      color: isDarkMode ? '#BFF7EF' : '#0F766E',
                                      border: `1px solid ${isDarkMode ? 'rgba(45,212,191,0.12)' : 'rgba(20,184,166,0.14)'}`,
                                    }}
                                  >
                                    {name}
                                  </span>
                                ))}
                              </div>
                            ) : null}

                            {isExpanded ? (
                              <div className="mt-3 space-y-2">
                                {waterways.length ? (
                                  <div className="grid grid-cols-1 gap-1.5 min-[430px]:grid-cols-3">
                                    {waterways.map((water: string) => (
                                      <div
                                        key={water}
                                        className="rounded-[14px] px-2.5 py-1.5 text-[10px] font-black uppercase tracking-[0.06em]"
                                        style={{
                                          background: isDarkMode ? 'rgba(124,199,255,0.10)' : 'rgba(234,247,255,0.78)',
                                          color: isDarkMode ? '#DFF1FF' : '#0D5B85',
                                          border: `1px solid ${isDarkMode ? 'rgba(124,199,255,0.16)' : 'rgba(124,199,255,0.24)'}`,
                                        }}
                                      >
                                        {water}
                                      </div>
                                    ))}
                                  </div>
                                ) : null}

                                {(r as any).pattern ? (
                                  <div
                                    className="rounded-[17px] px-3 py-2 text-[11.5px] font-semibold leading-relaxed"
                                    style={{
                                      background: isDarkMode ? 'rgba(255,255,255,0.055)' : 'rgba(255,255,255,0.68)',
                                      color: ui.textSecondary,
                                      border: `1px solid ${ui.border}`,
                                    }}
                                  >
                                    <div className="mb-1 text-[9px] font-black uppercase tracking-[0.16em]" style={textMuted}>How to read it</div>
                                    {(r as any).pattern}
                                  </div>
                                ) : null}

                                {starterMoves.length ? (
                                  <div className="space-y-1.5">
                                    {starterMoves.map((move: string, idx: number) => (
                                      <div key={move} className="flex items-start gap-2 text-[11px] font-bold leading-relaxed" style={textSecondary}>
                                        <span className="mt-[1px] grid h-4 w-4 shrink-0 place-items-center rounded-full text-[9px] font-black" style={{ background: isDarkMode ? 'rgba(122,232,184,0.12)' : 'rgba(220,252,231,0.86)', color: isDarkMode ? '#BDF7DE' : '#166534' }}>{idx + 1}</span>
                                        <span>{move}</span>
                                      </div>
                                    ))}
                                  </div>
                                ) : null}

                                {topSpecies.length ? (
                                  <div className="flex flex-wrap gap-1.5">
                                    {topSpecies.map((name: string) => (
                                      <button
                                        key={name}
                                        type="button"
                                        onClick={() => {
                                          setTab('species');
                                          setQuery(name);
                                        }}
                                        className="rounded-full px-2 py-0.5 text-[9.5px] font-black transition active:scale-95"
                                        style={{
                                          background: isDarkMode ? 'rgba(45,212,191,0.10)' : 'rgba(204,251,241,0.54)',
                                          color: isDarkMode ? '#BFF7EF' : '#0F766E',
                                          border: `1px solid ${isDarkMode ? 'rgba(45,212,191,0.14)' : 'rgba(20,184,166,0.16)'}`,
                                        }}
                                      >
                                        {name}
                                      </button>
                                    ))}
                                  </div>
                                ) : null}

                                {(r as any).note ? (
                                  <div className="text-[10.5px] font-bold leading-relaxed" style={textMuted}>
                                    {(r as any).note}
                                  </div>
                                ) : null}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
              ) : null}
              </div>
            </div>

          </div>
        </div>

        <div className="overflow-hidden rounded-[20px] p-3 sm:rounded-[20px] sm:p-4" style={panelStyle}>
          <div className="space-y-3">
            <div
              className="rounded-[18px] p-3"
              style={{
                ...surfaceStyle,
                background: isDarkMode
                  ? '#101827'
                  : '#FFFCF7',
              }}
            >
              <div className="space-y-3">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <div
                        className="rounded-full px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.16em]"
                        style={{
                          background: isDarkMode ? 'rgba(148,166,201,0.07)' : 'rgba(255,255,255,0.84)',
                          color: ui.textSecondary,
                          border: `1px solid ${ui.border}`,
                        }}
                      >
                        ⚡ Live Water Read
                      </div>
                      {signalsGeo5 ? (
                        <div
                          className="rounded-full px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.14em]"
                          style={{
                            background: signalsGated
                              ? isDarkMode
                                ? 'rgba(148,166,201,0.07)'
                                : 'rgba(255,255,255,0.82)'
                              : isDarkMode
                                ? 'rgba(124,199,255,0.16)'
                                : 'rgba(124,199,255,0.18)',
                            color: signalsGated ? (isDarkMode ? '#EAEFFF' : '#5B5348') : isDarkMode ? '#DFF1FF' : '#0E5B85',
                            border: `1px solid ${ui.border}`,
                          }}
                        >
                          {signalsGated ? 'Privacy mode' : 'Live bucket'}
                        </div>
                      ) : null}
                    </div>
                    <h2 className="mt-0.5 text-[18px] font-black min-[390px]:text-[20px]" style={textPrimary}>
                      Local water read
                    </h2>
                    <div className="mt-1 text-[12.5px] font-semibold" style={textSecondary}>
                      {signalsLoading
                        ? 'Checking nearby activity…'
                        : signalsGeo5
                          ? signalDelta != null
                            ? `${signalDelta > 0 ? `+${signalDelta}` : signalDelta} change in the current local read.`
                            : 'Live local signal is loaded for this area.'
                          : 'Live data stays off until you ask for it. Run a read when you want current local signal.'}
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={async () => {
                        if (!signalsExpanded) setSignalsExpanded(true);
                        await fetchSignalsNearMe({ forceRefresh: true });
                      }}
                      disabled={signalsLoading}
                      className="rounded-full px-3 py-1.5 text-[9.5px] font-black uppercase tracking-[0.12em] text-white transition hover:brightness-105 disabled:opacity-60"
                      style={{ background: '#22345B', boxShadow: '0 10px 20px rgba(0,0,0,0.14)' }}
                    >
                      {signalsLoading ? 'Checking…' : signalsGeo5 ? 'Refresh read' : 'Run live read'}
                    </button>

                    <button
                      type="button"
                      onClick={() => setSignalsExpanded((prev) => !prev)}
                      className="rounded-full px-3 py-1.5 text-[9.5px] font-black uppercase tracking-[0.12em] transition hover:brightness-105"
                      style={{
                        background: isDarkMode ? 'rgba(148,166,201,0.07)' : 'rgba(255,255,255,0.82)',
                        color: isDarkMode ? '#EAEFFF' : '#4D4A68',
                        border: `1px solid ${ui.border}`,
                        boxShadow: '0 10px 20px rgba(0,0,0,0.10)',
                      }}
                    >
                      {signalsExpanded ? 'Collapse' : 'Expand'}
                    </button>
                  </div>
                </div>

                {signalsExpanded ? (
                  <>
                    {signalsError ? (
                      <div className="rounded-[20px] p-3 text-sm" style={{ ...surfaceStyle, boxShadow: 'none', color: isDarkMode ? '#FFD8E0' : '#8A2945' }}>
                        {signalsError}
                      </div>
                    ) : null}

                    {signals.length > 0 ? (
                      <div className="space-y-2">
                        {signals.map((signal, index) => (
                          <button
                            key={signal.id}
                            type="button"
                            onClick={() => openSpecies(signal.speciesKey)}
                            className="rounded-[17px] px-3 py-2 text-left transition hover:-translate-y-[1px]"
                            style={{
                              ...surfaceStyle,
                              boxShadow: isDarkMode ? '0 10px 18px rgba(0,0,0,0.12)' : '0 8px 16px rgba(89,65,37,0.08)',
                              width: '100%',
                            }}
                          >
                            <div className="flex items-start gap-3">
                              <div
                                className="grid h-8 w-8 shrink-0 place-items-center rounded-[13px] text-[10px] font-black"
                                style={{
                                  background: isDarkMode ? 'rgba(124,199,255,0.14)' : 'rgba(223,241,255,0.86)',
                                  color: isDarkMode ? '#DFF1FF' : '#0d5b8a',
                                  border: `1px solid ${ui.border}`,
                                }}
                              >
                                {index === 0 ? '🔥' : index === 1 ? '⚡' : index === 2 ? '✨' : `#${index + 1}`}
                              </div>

                              <div className="min-w-0 flex-1">
                                <div className="flex items-start justify-between gap-3">
                                  <div className="min-w-0">
                                    <div className="truncate text-[14px] font-black" style={textPrimary}>
                                      {prettyKey(signal.speciesKey)}
                                    </div>
                                    <div className="mt-1 text-sm" style={textSecondary}>
                                      {prettyKey(signal.waterKey)} · {signal.count24h} catches · {signal.uniqueUsers24h} anglers
                                    </div>
                                  </div>
                                  <div className="shrink-0 text-xs font-bold" style={textMuted}>
                                    {hoursAgoRounded(signal.lastSeenAt)}
                                  </div>
                                </div>

                                <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                                  <span
                                    className="rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.08em]"
                                    style={{
                                      background: isDarkMode ? 'rgba(124,199,255,0.14)' : 'rgba(223,241,255,0.86)',
                                      color: isDarkMode ? '#DFF1FF' : '#0d5b8a',
                                      border: `1px solid ${isDarkMode ? 'rgba(124,199,255,0.18)' : 'rgba(124,199,255,0.30)'}`,
                                    }}
                                  >
                                    {getSignalActivityLabel(index, signal)}
                                  </span>
                                  {getSignalRuleLabel(signal) ? (
                                    <span
                                      className="rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.08em]"
                                      style={{
                                        background: isDarkMode ? 'rgba(255,227,155,0.14)' : 'rgba(255,243,207,0.88)',
                                        color: isDarkMode ? '#FFE39B' : '#A05A00',
                                        border: `1px solid ${isDarkMode ? 'rgba(255,227,155,0.18)' : 'rgba(241,215,143,0.36)'}`,
                                      }}
                                    >
                                      {getSignalRuleLabel(signal)}
                                    </span>
                                  ) : null}
                                  {signal.topWaterTypes.length ? (
                                    <span className="text-xs font-bold" style={textMuted}>
                                      {signal.topWaterTypes.slice(0, 2).map(prettyKey).join(' · ')}
                                    </span>
                                  ) : null}
                                </div>
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    ) : !signalsLoading && !signalsError ? (
                      <div className="rounded-[18px] p-4 text-sm" style={{ ...surfaceStyle, ...textSecondary, boxShadow: 'none' }}>
                        No local signal cards yet. Tap refresh to pull the closest water clues.
                      </div>
                    ) : null}
                  </>
                ) : (
                  <div className="rounded-[20px] p-3 text-sm" style={{ ...surfaceStyle, ...textSecondary, boxShadow: 'none' }}>
                    Signals are tucked away. Expand when you want the nearby bite pulse.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="overflow-hidden rounded-[20px] p-3 sm:rounded-[20px] sm:p-4" style={panelStyle}>
          <div className="space-y-3">
            <div
              className="rounded-[18px] px-4 py-3"
              style={{
                ...surfaceStyle,
                background: isDarkMode
                  ? '#101827'
                  : '#F8F2E9',
              }}
            >
              <div className="text-[11px] font-black uppercase tracking-[0.18em]" style={textMuted}>
                Stewardship note
              </div>
              <p className="mt-1 text-[12.5px] font-semibold leading-relaxed" style={textSecondary}>
                Roddo shows educational, probabilistic fishing signals from local patterns, public knowledge, and opt-in angler observations. It is not regulatory advice or an official stock assessment. Always check current local regulations before harvesting and practice safe access, careful handling, and leave-no-trace fishing.
              </p>
            </div>

            <FishIQPilotCard
              userProfile={userProfile}
              isDarkMode={isDarkMode}
              addToast={addToast}
            />
          </div>
        </div>


      </div>

      {!detail.isOpen && !isLoading && tab !== 'regions' && selectedSpeciesSummary &&
        ReactDOM.createPortal(
          <div
            className="roddo-safe-overlay fixed inset-0 z-[8998] flex items-center justify-center overflow-y-auto bg-[rgba(24,18,18,0.32)] px-4 backdrop-blur-[3px]"
            onClick={() => setSelectedSpeciesSummaryId(null)}
          >
            <div
              className="roddo-safe-modal-frame w-full max-w-[430px] overflow-y-auto overscroll-contain rounded-[20px] roddo-result-reveal [-webkit-overflow-scrolling:touch]"
              onClick={(event) => event.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-label="Water read species summary"
            >
              <FishIQSpeciesSummaryCard
                species={selectedSpeciesSummary}
                isDarkMode={isDarkMode}
                onClose={() => setSelectedSpeciesSummaryId(null)}
                onOpenDetail={() => {
                  const detailId = String((selectedSpeciesSummary as any).id || (selectedSpeciesSummary as any).slug || (selectedSpeciesSummary as any).commonName || '');
                  setSelectedSpeciesSummaryId(null);
                  if (detailId) openSpecies(detailId);
                }}
              />
            </div>
          </div>,
          document.body,
        )}


      <RoddoPickerSheet
        isOpen={pickerSheet === 'tab'}
        title="Water Read section"
        subtitle="Choose what you want to browse."
        value={tab}
        options={tabPickerOptions}
        onSelect={(value) => setTab(value as FishIQTab)}
        onClose={() => setPickerSheet(null)}
        isDarkMode={isDarkMode}
        accent="purple"
      />

      <RoddoPickerSheet
        isOpen={pickerSheet === 'region'}
        title="Study region"
        subtitle="Around Me stays local and static. Choose another region only when you want to explore it."
        value={studyRegionId}
        options={studyRegionOptions}
        onSelect={(value) => {
          if (String(value) === '__explore_regions__') {
            setShowAllStudyRegions(true);
            return false;
          }
          setStudyRegionId(String(value));
          setShowAllStudyRegions(false);
          return true;
        }}
        onClose={() => {
          setShowAllStudyRegions(false);
          setPickerSheet(null);
        }}
        isDarkMode={isDarkMode}
        accent="aqua"
      />

      <RoddoPickerSheet
        isOpen={pickerSheet === 'aquatic'}
        title="Target type"
        subtitle="Keep the scout list focused."
        value={aquaticFilter}
        options={aquaticFilterOptions}
        onSelect={(value) => setAquaticFilter(value as AquaticFilter)}
        onClose={() => setPickerSheet(null)}
        isDarkMode={isDarkMode}
        accent="purple"
      />

      <RoddoPickerSheet
        isOpen={pickerSheet === 'water'}
        title="Water type"
        subtitle="Match the list to the water you are fishing."
        value={waterTypeFilter}
        options={waterTypeOptions}
        onSelect={(value) => setWaterTypeFilter(value as WaterTypeFilter)}
        onClose={() => setPickerSheet(null)}
        isDarkMode={isDarkMode}
        accent="aqua"
      />


      {detail.isOpen &&
        ReactDOM.createPortal(
          <div
            ref={modalScrollRef}
            className="roddo-fishiq-detail-modal roddo-safe-overlay fixed inset-0 z-[9999] overflow-hidden bg-[rgba(9,13,24,0.72)] px-0 backdrop-blur-md"
            onClick={closeDetail}
          >
            <div className="flex h-full w-full items-stretch justify-center">
              <div
                className="flex h-full w-full max-w-[960px] min-h-0 flex-col px-3 sm:px-5"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="mb-3 shrink-0 px-1">
                  <div className="text-[10px] font-black uppercase tracking-[0.24em] text-white/70">Water read detail</div>
                  <div className="mt-1 text-sm font-semibold text-white/90">Rolodex card to full detail view</div>
                </div>

                <div className="roddo-fishiq-detail-shell min-h-0 flex-1 overflow-hidden rounded-[34px]">
                  <FishIQDetailView
                    isOpen={detail.isOpen}
                    kind={detail.kind}
                    id={detail.id}
                    species={selectedSpecies}
                    region={selectedRegion}
                    onClose={closeDetail}
                    catchLog={catchLog}
                    viewerUserId={userProfile?.uid || null}
                    isSaved={
                      detail.kind === 'species'
                        ? !!(selectedSpecies && isFishIQItemSaved(selectedSpecies, savedSpeciesIds))
                        : !!(selectedRegion && isFishIQItemSaved(selectedRegion, savedRegionIds))
                    }
                    onToggleSaved={() => {
                      if (detail.kind === 'species' && selectedSpecies) {
                        toggleSpeciesSaved(getFishIQSavedToggleKey(selectedSpecies, savedSpeciesIds));
                        return;
                      }

                      if (detail.kind === 'region' && selectedRegion) {
                        toggleRegionSaved(getFishIQSavedToggleKey(selectedRegion, savedRegionIds));
                      }
                    }}
                    onSelectSpecies={(speciesName, payload) => {
                      trackRoddoEvent('fishiq_log_this_fish', { source: 'fishiq_detail' });
                      onSelectSpecies?.(speciesName, payload);
                    }}
                    mapContext={fishiqLocationContext}
                    isDarkMode={isDarkMode}
                    variant="modal"
                    maxBodyHeightClassName="max-h-full"
                  />
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </RoddoPageShell>
  );
};

export default FishIQView;
